"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { useFeatureFlagVariantKey } from "posthog-js/react"
import Link from "next/link"
import Image from "next/image"
import { absoluteBrandAssetUrl, BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import {
  Mail,
  Loader2,
  CheckCircle2,
  Lock,
  Star,
  ArrowRight,
  ArrowUpRight,
  Leaf,
  AlertTriangle,
  ExternalLink,
  Share2,
  Copy,
  Check,
  Clock,
  Bookmark,
  Droplets,
  FlaskConical,
  Scissors,
  BookOpen,
  Snowflake,
  Sun,
  SprayCan,
  Sprout,
  XCircle,
  X,
  type LucideIcon,
} from "lucide-react"
import { GrassTuft } from "@/components/icons/grass-tuft"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { PdfExportButton } from "@/components/lawn-products-guide/pdf-export-button"
import { Reveal, Stagger, HoverLift, CountUp } from "@/components/motion"
import { motion, MotionConfig, useReducedMotion } from "motion/react"
import { FADE_TRANSITION, FADE_TRANSITION_SOFT } from "@/lib/motion-presets"
import { useExitIntent } from "@/hooks/useExitIntent"
import { cn } from "@/lib/utils"
import { analytics, contentAnalytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { suggestEmail } from "@/lib/email-typo"
import { PATTERNS } from "@/lib/form-validation"

const UNLOCK_KEY = "wgit_products_guide_2026_unlocked"
const EMAIL_DRAFT_KEY = "wgit_products_guide_2026_email_draft"

// Persist the in-progress email so a navigation-away doesn't lose the entry.
// Swallows privacy-mode storage errors.
function saveEmailDraft(value: string): void {
  try {
    if (typeof window === "undefined") return
    if (value) {
      window.localStorage.setItem(EMAIL_DRAFT_KEY, value)
    } else {
      window.localStorage.removeItem(EMAIL_DRAFT_KEY)
    }
  } catch {
    // No-op in privacy mode.
  }
}
const PAGE_URL = "https://whatgrassisthis.com/lawn-products-guide"
const SHARE_TITLE = "The 2026 Lawn Products Buyer's Guide"
const SHARE_TEXT =
  "Honest picks for the products that actually move a lawn in 2026. Pre-emergents, fertilizers, sprayers, soil tests, and what NOT to buy."

import { amazonUrl, amazonSearchUrl as amazonSearch } from "@/lib/affiliate"

function amazonImg(id: string): string {
  return `https://m.media-amazon.com/images/I/${id}._AC_SL200_.jpg`
}

// Home Depot affiliate via Impact (impact.com). When both env vars are set, raw
// HD URLs get wrapped in the Impact deeplink format that attributes the click
// to our publisher account. 15497 is Home Depot's fixed Impact advertiser ID.
// Until approved, links pass through unchanged with a plain rel attribute.
const HOMEDEPOT_IMPACT_PUBLISHER_ID = process.env.NEXT_PUBLIC_HOMEDEPOT_IMPACT_PUBLISHER_ID
const HOMEDEPOT_IMPACT_CREATIVE_ID = process.env.NEXT_PUBLIC_HOMEDEPOT_IMPACT_CREATIVE_ID
const HOMEDEPOT_AFFILIATE_ACTIVE = Boolean(
  HOMEDEPOT_IMPACT_PUBLISHER_ID && HOMEDEPOT_IMPACT_CREATIVE_ID,
)

function homeDepotUrl(rawUrl: string): string {
  if (!HOMEDEPOT_AFFILIATE_ACTIVE) return rawUrl
  return `https://homedepot.sjv.io/c/${HOMEDEPOT_IMPACT_PUBLISHER_ID}/${HOMEDEPOT_IMPACT_CREATIVE_ID}/15497?u=${encodeURIComponent(rawUrl)}`
}

type Tier = "Budget" | "Best Value" | "Pro"
type Badge =
  | "Editor's Pick"
  | "Cult Favorite"
  | "Beginner Friendly"
  | "Pro Approved"
  | "Best Long-Term Value"
  | null

interface ProductPick {
  tier: Tier
  name: string
  blurb: string
  price?: string
  amazon: string
  imageUrl?: string
  badge?: Badge
  isSearch?: boolean
}

interface GuideSection {
  id: string
  number: number
  title: string
  subtitle: string
  verdict?: string
  intro: string
  picks?: ProductPick[]
  cta?: {
    label: string
    url: string
    vendor: string
  }
  avoid?: string[]
  tip?: string
  preview?: boolean
}

// Helper to build the picks with verified ASINs from data/guide-product-recommendations.ts
// and data/affiliate-product-catalog.ts. Search fallbacks earn affiliate commission for
// categories we don't have catalog ASINs for (sprayers, soil tests, mowers).
const SECTIONS: GuideSection[] = [
  {
    id: "pre-emergent",
    number: 3,
    title: "Pre-emergent: the single best dollar in lawn care",
    subtitle: "Stop crabgrass and Poa annua before they germinate",
    verdict: "Buy this: Barricade 4FL or generic prodiamine. Skip Scotts step-style combo products.",
    intro:
      "If you only buy one product this year, this is the one. Pre-emergents form a chemical barrier in the top inch of soil that prevents weed seeds from rooting after they sprout. Apply once in spring before soil temps hit 55°F at the top inch of soil (roughly when forsythias bloom) and again in late summer when temps drop back through 70°F. Two applications a year control crabgrass on most lawns.",
    picks: [
      {
        tier: "Best Value",
        name: "Barricade 4FL Herbicide Concentrate",
        blurb:
          "Liquid prodiamine, one of the gold-standard pre-emergent actives. One 4 oz bottle treats roughly half an acre at label rate. Mix with water in any backpack sprayer. Two apps a year and crabgrass stops being your problem.",
        price: "~$30",
        amazon: amazonUrl("B00OXD2CKG"),
        imageUrl: amazonImg("71Fl3q83ubL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Pro",
        name: "Dimension 2EW",
        blurb:
          "Dithiopyr instead of prodiamine. Slightly broader weed spectrum and includes some early post-emergent activity on young crabgrass that snuck through. Worth the upgrade if you've had escapes.",
        price: "~$158",
        amazon: amazonUrl("B0051GXXPW"),
        imageUrl: amazonImg("41Tp97DV1QL"),
      },
      {
        tier: "Budget",
        name: "Scotts Halts Crabgrass & Grassy Weed Preventer",
        blurb:
          "Granular pendimethalin. Goes through any spreader, no sprayer needed. You pay for the limestone filler, but it works and beats nothing. The right call if you don't own a sprayer yet.",
        price: "~$47",
        amazon: amazonUrl("B08L6T3B4H"),
        imageUrl: amazonImg("5196bQGYE8L"),
        badge: "Beginner Friendly",
      },
    ],
    tip: "Calibrate your sprayer first. Walk a measured 1,000 sqft at your normal pace and check how much water you use. Then mix prodiamine at the label rate per 1,000 sqft. Skipping calibration is why most DIY pre-emergent apps fail.",
  },
  {
    id: "fertilizer",
    number: 1,
    title: "Granular fertilizer worth your money",
    subtitle: "Slow-release nitrogen, not the cheap stuff",
    verdict: "Buy this: PGF Complete or Milorganite. Skip Scotts 4-Step and any weed-and-feed.",
    preview: true,
    intro:
      "Cheap bagged fertilizer dumps fast-release urea, gives you two weeks of green, and burns out. Better products use slow-release nitrogen sources (methylene urea, polymer-coated urea, biosolids) that feed steadily for weeks (polymer-coated urea ~8–14 weeks, methylene urea longer). Cool-season lawns need nitrogen mostly in spring and fall. Warm-season lawns get it all summer.",
    picks: [
      {
        tier: "Best Value",
        name: "The Andersons PGF Complete 16-4-8",
        blurb:
          "Premium homeowner-targeted blend with humic acid and 1.5% iron. Treats 12,500 sqft per 50 lb bag. Cleaner application than Lesco, better color than Milorganite. The current gold standard for cool-season feeding.",
        price: "~$60",
        amazon: amazonUrl("B07SYLCKRH"),
        imageUrl: amazonImg("41xelE+7eJL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Best Value",
        name: "Milorganite 6-4-0",
        blurb:
          "Slow-release biosolid nitrogen with 4% iron for color. Very low burn risk thanks to a near-zero salt index, treats 2,500 sqft per 36 lb bag, smells the first day. The default cool-season pick for a reason; works just as well on warm season.",
        price: "~$35",
        // 2026-07-11: swapped from OOS B0DDHTJKCY to the in-stock 32 lb listing
        // (B0FGDNF55M, $34.84). Same product, same size; consolidates Milorganite.
        amazon: amazonUrl("B0FGDNF55M"),
        imageUrl: amazonImg("71nBKo4i15L"),
        badge: "Cult Favorite",
      },
      {
        tier: "Pro",
        name: "Neighbor's Envy 7-in-1 Liquid Fertilizer",
        blurb:
          "All-in-one liquid concentrate with nitrogen, iron, humic acid, and sea kelp. The 64 oz bottle covers up to 8,000 sqft. The convenience pick when you don't want to spread granules.",
        price: "~$40",
        amazon: amazonUrl("B0GYJLDD5J"),
        imageUrl: amazonImg("41zvES8yVvL"),
      },
      {
        tier: "Budget",
        name: "Scotts Turf Builder Starter Food for New Grass",
        blurb:
          "The right pick when you're seeding, overseeding, or laying sod. High phosphorus to push root development. Don't use this as your regular fert, only at planting.",
        price: "~$30",
        amazon: amazonUrl("B00DK49DVA"),
        imageUrl: amazonImg("81oY9zpw+qL"),
        badge: "Beginner Friendly",
      },
    ],
    avoid: [
      "Scotts 4-Step program: expensive, wrong timing for most zones, locks you into one brand for the year",
      "Anything labeled 'weed and feed': pre-emergent and fertilizer rarely share the same ideal week, and the combo locks you into a compromise on both",
      "Sta-Green and other big-box house brands: high fast-release urea, weak slow-release fraction",
    ],
    tip: "Match your nitrogen to your grass. Cool-season target is roughly 3 to 4 lbs N per 1,000 sqft per year split across spring and fall. Warm-season varies by species: bermuda 3 to 6 lbs, St. Augustine 2 to 4, centipede only 1 to 2 (over-fertilizing centipede actively damages it). The bag tells you N per sqft if you do the math.",
  },
  {
    id: "post-emergent",
    number: 2,
    title: "Post-emergent herbicides for tough weeds",
    subtitle: "Spot-spray, not blanket spray",
    verdict: "Cool-season buy: Tenacity. Warm-season buy: IMAGE Southern. Tough-weed pro: T-Zone SE.",
    preview: true,
    intro:
      "Pre-emergent is preventative. Post-emergent kills what got through. The trick is matching the herbicide to your grass type, because most herbicides that nuke broadleaf weeds will also nuke your warm-season turf. Spot-spray with a backpack or pump sprayer; never blanket-apply unless you genuinely have weeds everywhere.",
    picks: [
      {
        tier: "Best Value",
        name: "Tenacity Turf Herbicide 8 oz",
        blurb:
          "Mesotrione, safe on cool-season grasses (KBG, tall fescue, ryegrass; established fine fescue is fine, but skip it on newly seeded fine fescue). Per the EPA label: controls 46+ broadleaf and grass species including nimblewill and creeping bentgrass. Signature symptom: treated weeds lose chlorophyll, turn white, and die over ~3 weeks. Magic in a bottle.",
        price: "~$62",
        amazon: amazonUrl("B005DUTNF0"),
        imageUrl: amazonImg("7150ZVCDOfL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Best Value",
        name: "Southern Ag Atrazine for Southern Lawns (1 Gallon)",
        blurb:
          "Per label: safe on St. Augustine (including Floratam), centipedegrass, and zoysia. Same atrazine active ingredient used in IMAGE Southern — the southern grasses that can't take most broadleaf killers. The everyday warm-season pick.",
        price: "~$38",
        amazon: amazonUrl("B007259YS0"),
        imageUrl: amazonImg("41+qBK7deIL"),
        badge: "Beginner Friendly",
      },
      {
        tier: "Pro",
        name: "T-Zone SE Broadleaf Herbicide",
        blurb:
          "Four-way blend (triclopyr + sulfentrazone + 2,4-D + dicamba) labeled for 60+ broadleaf species. Especially good on the weeds that survive everything else: wild violet, ground ivy (creeping charlie), oxalis, mature clover, and black medic. One bottle lasts most homeowners 5 years.",
        price: "~$37",
        amazon: amazonUrl("B00NWSIHCK"),
        imageUrl: amazonImg("61Y+uzC4D+L"),
        badge: "Pro Approved",
      },
      {
        tier: "Best Value",
        name: "SpeedZone EW Lawn Weed Killer",
        blurb:
          "Fast-acting cool-season broadleaf. Targets dandelion, clover, spurge, plantain. Visible knockdown in 24 to 48 hours, faster than Trimec or Tenacity for most listed weeds.",
        price: "~$39",
        amazon: amazonUrl("B001PCRKDC"),
        imageUrl: amazonImg("411cLnK5YAS"),
      },
    ],
    avoid: [
      "Ortho Weed B Gon and Spectracide Weed Stop in the ready-to-spray bottles: same actives as the pro products at 5 to 10x the cost per acre treated",
      "Roundup on your lawn: non-selective, kills the grass too",
      "'Vinegar weed killer' as a serious option: top-kills annuals, does not kill roots, also kills your grass on contact",
    ],
    tip: "Always add a non-ionic surfactant (NIS, ~$15/qt) to herbicide mixes. It's a cheap, meaningful efficacy boost for most herbicides. Skip it and you are spraying water that beads off the leaves.",
  },
  {
    id: "sprayer",
    number: 4,
    title: "The sprayer upgrade that pays for itself",
    subtitle: "Stop using the trigger sprayer from the herbicide bottle",
    verdict: "Buy this: Chapin 4-gallon ProSeries (or Solo 425 if you can stretch).",
    intro:
      "The single biggest leap in DIY lawn care is going from a ready-to-spray bottle to a real backpack sprayer. You unlock concentrate pricing (which is 90 percent cheaper per gallon), even application, and accurate label rates. A $60 sprayer pays for itself on the second herbicide app.",
    picks: [
      {
        tier: "Budget",
        name: "Chapin 61500 4-Gallon Made in USA Backpack Sprayer",
        blurb:
          "Diaphragm pump, padded straps, three nozzles included. The default starter backpack. Lasts 3 to 5 years with proper rinsing after each use. Where almost every lawn DIYer starts.",
        price: "~$56",
        amazon: amazonUrl("B005FPUHEE"),
        imageUrl: amazonImg("41x670W-H9L"),
        badge: "Beginner Friendly",
      },
      {
        tier: "Best Value",
        name: "SOLO 425 4-Gallon Piston Backpack Sprayer",
        blurb:
          "German-engineered piston pump (up to 90 psi), lockable shut-off, better balanced under load. Will outlast the Chapin by years. The Honda Civic of backpacks: not exciting, never breaks.",
        price: "~$97",
        amazon: amazonUrl("B00002N6BW"),
        imageUrl: amazonImg("41CLxjKzfdL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Pro",
        name: "Chapin 60124 4-Gallon 20V Battery Backpack Sprayer",
        blurb:
          "No more pumping. The 20V lithium-ion battery gives you consistent output for the entire tank (up to ~1.75 hours per charge), which dramatically improves application accuracy. Anti-clog filtration, brass/poly nozzles. Worth every penny the first time you spray an acre.",
        price: "~$140",
        amazon: amazonUrl("B0BYB6P8J3"),
        imageUrl: amazonImg("31pZevIGTlL"),
        badge: "Pro Approved",
      },
    ],
    tip: "Buy a TeeJet AIXR 11003 flat-fan nozzle ($8) and a TeeJet flood-jet tip. Most stock nozzles are coarse cones built for general pesticide use. Flat fans give you the even overlap pattern herbicides need.",
  },
  {
    id: "soil-test",
    number: 5,
    title: "Soil testing: stop guessing what your lawn needs",
    subtitle: "$10 to $30 that saves you hundreds in wrong product",
    verdict: "Buy this: your state extension lab ($4–15) or MySoil ($35) if you want easy mode.",
    intro:
      "Every lawn care decision (lime vs sulfur, fertilizer ratio, iron app, gypsum, soft acid amendments) is downstream of pH and CEC. Guessing costs money. A proper soil test costs less than one bag of fertilizer and tells you exactly what you actually need.",
    picks: [
      {
        tier: "Best Value",
        name: "MySoil Soil Test Kit",
        blurb:
          "Mails you a prepaid kit, you ship back soil, they email a report in 6 to 10 days. Tests 13 nutrients and pH with tailored recommendations. Easier than coordinating with the extension office. Best entry-level mail-in lab.",
        price: "~$32",
        amazon: amazonUrl("B084TSNR79"),
        imageUrl: amazonImg("51P92jDJmUL"),
        badge: "Beginner Friendly",
      },
      {
        tier: "Pro",
        name: "Soil Savvy Soil Test Kit",
        blurb:
          "Uses an ion-exchange resin capsule instead of a soil chunk, so the numbers reflect what your plants will actually pull. Different (often more useful) numbers. Includes a full nutrient analysis with a fertilizer recommendation. Some pros prefer it.",
        price: "~$27",
        amazon: amazonUrl("B01GIMOG8A"),
        imageUrl: amazonImg("51BqnmxTUrL"),
        badge: "Pro Approved",
      },
    ],
    avoid: [
      "Cheap pH probe sticks from Amazon: not just inaccurate, often actively wrong",
      "Big-box four-in-one meters: read soil moisture loosely, everything else is theater",
      "Salt-based pH 'test strips' for soil: built for hydroponics, not real soil",
    ],
    tip: "Pull soil from 6 to 10 spots across your lawn, 4 to 6 inches deep, mix in a clean bucket, submit one composite sample. One spot can be wildly off because of where a tree used to be or where the builder dumped fill.",
  },
  {
    id: "spreader",
    number: 6,
    title: "Spreaders: budget vs pro",
    subtitle: "The cheap one is fine until it isn't",
    verdict: "Buy this: Scotts EdgeGuard Mini under 5,000 sqft; Earthway 2150 above.",
    intro:
      "A spreader is a tool you replace once every 10 years if you choose right, or every 2 years if you choose wrong. Drop spreaders are precise but slow. Rotary (broadcast) spreaders are fast but require overlap discipline. For 95 percent of homeowners, a rotary is the right call.",
    picks: [
      {
        tier: "Budget",
        name: "Scotts Turf Builder EdgeGuard Mini Broadcast Spreader",
        blurb:
          "Holds 5,000 sqft worth of product. Plastic body, plastic gears, but EdgeGuard side blocker is genuinely useful along driveways. Right call for lawns under 5,000 sqft.",
        price: "~$60",
        amazon: amazonUrl("B002YPS1KK"),
        imageUrl: amazonImg("81NLk-gC4YS"),
        badge: "Beginner Friendly",
      },
      {
        tier: "Best Value",
        name: "Earthway 2150 Commercial 50 lb Broadcast Spreader",
        blurb:
          "Heavy-duty steel frame, 13-inch pneumatic stud tires, adjustable drop rate. Walk-behind push design. The crossover between consumer and pro. Lasts a decade with basic care, holds 50 lbs.",
        price: "~$331",
        amazon: amazonUrl("B00002N66D"),
        imageUrl: amazonImg("41l1rsiy9pL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Pro",
        name: "Scotts Elite Spreader",
        blurb:
          "Scotts' top-tier homeowner spreader. EdgeGuard, dual rotors, large pneumatic tires, holds 20,000 sqft worth. The right step up before Lesco/Spyker territory.",
        price: "~$170",
        amazon: amazonUrl("B077JBGG4H"),
        imageUrl: amazonImg("81NLk-gC4YS"),
        badge: "Best Long-Term Value",
      },
    ],
    tip: "Always do two passes at half the rate, perpendicular to each other. Single-pass spreader patterns are never as even as the marketing claims, and any miss shows up as a stripe in your lawn 10 days later.",
  },
  {
    id: "seed",
    number: 7,
    title: "Seed brands that actually contain what's on the label",
    subtitle: "Read the seed label, not the bag art",
    verdict: "Cool-season buy: Jonathan Green Black Beauty Ultra or NightShift TTTF. Warm-season buy: Scotts Bermudagrass.",
    intro:
      "The seed industry runs on lies of omission. A 'premium' bag mix often contains a large fraction of annual ryegrass nurse crop that dies after one season. The real test is the seed label on the back: look for named cultivars, not generic types. Named cultivars have actual NTEP performance data behind them.",
    picks: [
      {
        tier: "Best Value",
        name: "Jonathan Green Black Beauty Ultra Grass Seed",
        blurb:
          "Real cultivar blend (mostly tall fescue), big-box availability, no annual ryegrass filler. Solid pick for a transition-zone overseed.",
        price: "~$41",
        amazon: amazonUrl("B003AYWIU8"),
        imageUrl: amazonImg("71I5+E4pKxL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Pro",
        name: "NightShift Turf Type Tall Fescue Seed 25 lb",
        blurb:
          "Premium TTTF for deep-rooted, drought-tolerant cool-season lawns. Great for renovation. The cult-favorite pick from the serious DIY community.",
        price: "~$150",
        amazon: amazonUrl("B0D9LFN68G"),
        imageUrl: amazonImg("71Mc0DPOgeL"),
        badge: "Cult Favorite",
      },
      {
        tier: "Pro",
        name: "NightShift Kentucky Bluegrass Seed 25 lb",
        blurb:
          "Premium KBG blend for thick, self-repairing cool-season lawns with excellent cold tolerance. If you want the dark-green KBG look, start here.",
        price: "~$215",
        amazon: amazonUrl("B0D9L9Z15K"),
        imageUrl: amazonImg("71WkeJOAfdL"),
        badge: "Pro Approved",
      },
      {
        tier: "Best Value",
        name: "Scotts Turf Builder Grass Seed Bermudagrass",
        blurb:
          "Heat-loving Bermuda seed for patching or starting common Bermuda lawns in full sun. Best big-box warm-season pick that isn't a junk mix.",
        price: "~$22",
        amazon: amazonUrl("B00D4AESXS"),
        imageUrl: amazonImg("81XYgtoiMNL"),
        badge: "Beginner Friendly",
      },
    ],
    avoid: [
      "Anything with 'annual ryegrass' or 'Italian ryegrass' on the label: it dies in 12 months",
      "Pennington Smart Seed: heavily marketed mix, generic cultivars, mostly fluff",
      "Scotts EZ Seed: 90 percent mulch coating by volume, you are paying for sawdust",
    ],
    tip: "Check the seed label for 'inert matter' and 'other crop' percentages. Real premium seed is under 1 percent inert and 0 percent other crop. Anything above 3 percent inert is being sold by volume, not by viable seed count.",
  },
  {
    id: "iron",
    number: 8,
    title: "Iron and color: the green-up products",
    subtitle: "Cheap visual wins, real plant benefit",
    verdict: "Buy this: PetraTools Liquid Iron for color without the burn risk.",
    intro:
      "Iron makes lawns dark green without pushing nitrogen growth. That means more curb appeal without faster mowing. Foliar iron (sprayed on the leaf) is the fastest visible effect in lawn care; you can usually see it darken within a day or two. Use sparingly, because too much will stain concrete and walkways orange.",
    picks: [
      {
        tier: "Best Value",
        name: "PetraTools Liquid Iron for Lawns",
        blurb:
          "Chelated liquid iron for deep green color without pushing nitrogen growth. EDTA-free, 32 oz concentrate. Sprays from any backpack at 3 to 5 oz per 1,000 sqft.",
        price: "~$17",
        amazon: amazonUrl("B095VQCL85"),
        imageUrl: amazonImg("81CoCGsDkxL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Budget",
        name: "Southern Ag Chelated Liquid Iron (16 oz)",
        blurb:
          "Lower-cost chelated iron supplement for deeper green color without the surge of nitrogen growth in hot weather. Great gateway product to foliar feeding.",
        price: "~$15",
        amazon: amazonUrl("B00A528VOK"),
        imageUrl: amazonImg("41vAc2iKM1L"),
        badge: "Beginner Friendly",
      },
    ],
    avoid: [
      "Ironite as your only fertilizer: the iron is fine, the 1-0-1 NPK ratio is useless",
      "Powder iron sulfate from the garden store: stains everything within 20 feet and is hard to mix evenly",
    ],
    tip: "Spray iron on calm cool mornings, mow after the spray has fully dried (4+ hours), and avoid concrete. Stain remover for accidental drift: a paste of dish soap plus lemon juice, scrubbed within an hour.",
  },
  {
    id: "mower",
    number: 9,
    title: "Mowers: rotary, reel, battery",
    subtitle: "The decision that shapes everything else",
    verdict: "Buy this: EGO LM2156SP for most homeowners. Honda HRX-BV for premium walk-behind. McLane reel for low-cut bermuda.",
    intro:
      "Your mower decides what grass you can grow at what height, which decides your fertilizer and water needs, which decides your weeds. Reel mowers cut bermuda and zoysia below 1 inch (the height where they look like a putting green). Rotary mowers handle KBG, fescue, and ryegrass at 2 to 4 inches. Battery has caught up to gas for residential use under 10,000 sqft.",
    picks: [
      {
        tier: "Best Value",
        name: "EGO POWER+ LM2156SP 56V Self-Propelled Mower",
        blurb:
          "Best-in-class battery rotary. 21-inch deck, Select Cut multi-blade system, real self-propel, 60+ minute runtime with the included 10 Ah battery and 700W turbo charger. No oil, no gas, starts every time. The default modern pick under 10,000 sqft.",
        price: "~$879",
        amazon: amazonUrl("B08YS8WQDB"),
        imageUrl: amazonImg("31jaMtvuNOL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Pro",
        name: "Honda HRX-BV Battery Self-Propelled Mower",
        blurb:
          "Honda's premium walk-behind, now battery. 21-inch deck, variable-speed self-propel, MicroCut twin blades, 2.2 bushel bag. Honda build quality with no oil changes, no gas, no pull-cord. Replaces the discontinued HRX217VKA.",
        price: "~$1,049",
        amazon: amazonUrl("B0G3CZMVT9"),
        imageUrl: amazonImg("41KGr4vq+3L"),
        badge: "Pro Approved",
      },
      {
        tier: "Pro",
        name: "Toro 60V eTimeMaster 30-inch",
        blurb:
          "30-inch deck, Personal Pace self-propel, 60V battery platform. Cuts your mow time by a third for half-acre yards. The right call for 15,000 to 30,000 sqft. Modern battery replacement for the gas TimeMaster.",
        price: "~$1,500",
        amazon: amazonUrl("B0DQRM42BB"),
        imageUrl: amazonImg("41Qaa8K32DL"),
        badge: "Best Long-Term Value",
      },
      {
        tier: "Pro",
        name: "McLane 25 in. 7-Blade Gas Reel Mower (9.5GT B&S)",
        blurb:
          "For bermuda or zoysia at under 1 inch. 7-blade reel, 9.5 GT Briggs & Stratton engine, grooved roller, real cylinder cut. Required gear if you want golf-green-style turf at home. Premium pricing matches premium hardware.",
        price: "~$2,900",
        amazon: amazonUrl("B0GKQBXZJZ"),
        imageUrl: amazonImg("415IaMN0ucL"),
        badge: "Cult Favorite",
      },
    ],
    avoid: [
      "Cheap manual reel mowers under $150: the blades dull within a season and cannot be re-sharpened easily",
      "Big-box battery push mowers under $300: weak motors, undersized batteries, frustrating runtime",
    ],
    tip: "Never cut more than the top one-third of the blade in a single mow. If you have been gone two weeks and the grass is shaggy, mow at a high setting first, then come back two days later at your target height.",
  },
  {
    id: "aeration",
    number: 10,
    title: "Aeration: rent the tool, do not buy",
    subtitle: "Almost nothing you can buy outperforms a rented core aerator",
    verdict: "Don't buy. Rent a core aerator from Home Depot for ~$90/day.",
    intro:
      "Core aeration (pulling soil plugs out, not poking holes) is the highest-impact one-day project for a tired lawn. It relieves compaction, opens the soil for water and air, and gives overseed a place to actually germinate. The catch is that the tools homeowners can buy are mostly garbage; the real machines weigh 300 lbs and rent for under $100 a day.",
    cta: {
      label: "Find a core aerator rental at Home Depot",
      url: "https://www.homedepot.com/s/plug%20aerator%20rental",
      vendor: "homedepot",
    },
    avoid: [
      "Spike aerators (rolling drum with spikes): compresses soil sideways, makes things worse not better",
      "Manual plug aerators (long-handled foot tool): you will get 50 plugs done before your back gives out",
      "'Liquid aeration' products: research support is thin, anecdotal at best, marketing at worst",
      "Tow-behind plug aerators with weights you sit on: residential ones do not penetrate dense soil",
    ],
    tip: "Aerate when the soil is moist but not wet, two passes perpendicular to each other. Leave the plugs on the surface; they break down in 2 to 3 weeks and return nutrients to the soil. Aerate cool-season in early fall, warm-season in late spring.",
  },
  {
    id: "irrigation",
    number: 11,
    title: "Smart irrigation: the controller upgrade",
    subtitle: "Most water savings come from the brain, not the sprinkler",
    verdict: "Buy this: Rachio 3 + MP Rotator nozzles. Skip the dumb timer.",
    intro:
      "If you already have an irrigation system, swapping the old timer for a smart controller cuts water use 20 to 50 percent. The controller pulls local weather and skips runs when the soil is wet, which standard timers never do.",
    picks: [
      {
        tier: "Best Value",
        name: "Rachio 3 Smart Sprinkler Controller 8-Zone",
        blurb:
          "Cleanest app, easiest install, no required subscription. Weather-adaptive scheduling that saves 20 to 50 percent on water. Works with Alexa, Google, HomeKit. Best smart controller, period.",
        price: "~$185",
        amazon: amazonUrl("B07CZ864Y9"),
        imageUrl: amazonImg("71asdFrVrlL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Budget",
        name: "RAINPOINT 3-in-1 WiFi Soil Moisture Sensor",
        blurb:
          "Wireless soil sensor that monitors moisture, temperature, and light with phone alerts. Know exactly when your lawn needs water vs when it doesn't.",
        price: "~$40",
        amazon: amazonUrl("B0D9PW8Q2H"),
        imageUrl: amazonImg("61SxfjfDGlL"),
        badge: "Beginner Friendly",
      },
    ],
    tip: "Run your irrigation once before sunrise, not in the evening. Evening watering keeps leaves wet overnight, which encourages fungus (brown patch in summer, dollar spot anytime). Pre-dawn watering dries off by 10 AM with no fungus risk.",
  },
  {
    id: "wetting-agents",
    number: 12,
    title: "Wetting agents and surfactants",
    subtitle: "The quiet performance hack",
    verdict: "Buy this: Hydretain for serious summer stress. GS Kelp+Yucca as a gateway product.",
    intro:
      "Wetting agents (also called soil surfactants) reduce the surface tension of water in soil so it spreads laterally instead of channeling straight down. In practice that means more even moisture, less hydrophobic spots, deeper rooting. The pro lawn care world quietly relies on these; the homeowner world barely knows they exist.",
    picks: [
      {
        tier: "Pro",
        name: "Hydretain Root Zone Moisture Manager",
        blurb:
          "Humectant blend that pulls water vapor from the soil air space into the root zone. Manufacturer trials show roughly 50% water reduction in summer heat. One app typically lasts ~3 months.",
        price: "~$115",
        amazon: amazonUrl("B099FJY4YT"),
        imageUrl: amazonImg("81hzVv9xLLL"),
        badge: "Editor's Pick",
      },
      {
        tier: "Budget",
        name: "GS Plant Foods Kelp + Yucca Wetting Agent",
        blurb:
          "Natural wetting agent that improves water penetration and heat/drought stress tolerance. The gateway product if you don't want to commit to Hydretain pricing.",
        price: "~$25",
        amazon: amazonUrl("B0BSLTXHQ3"),
        imageUrl: amazonImg("71YEgPiL+7L"),
        badge: "Beginner Friendly",
      },
    ],
    tip: "Apply wetting agents before you have a hydrophobic problem, not after. Once the soil dries out and waxes off, even strong surfactants take 2 to 3 apps to fully rehydrate the profile.",
  },
  {
    id: "avoid",
    number: 13,
    title: "What NOT to buy in 2026",
    subtitle: "The shortlist of products that waste money or actively hurt your lawn",
    intro:
      "Most lawn-care marketing is sold to people who do not have time to research, and a surprising number of products are net-negative. Skipping these is worth as much as buying the right products in the sections above.",
    avoid: [
      "Scotts 4-Step program: locks you into wrong-time apps for most regions, expensive per acre, and the consumer formulations are the watered-down versions of better pro products",
      "Step-style 'weed and feed' combos: pre-emergent and fertilizer rarely share the same ideal week, and the combo locks you into a compromise on both",
      "'Lawn paint': vanity product, washes off in two rains, and the surfactants can damage already-stressed turf",
      "Cheap reel mowers under $200: the blades dull in one season and the bedknife adjustment is too coarse for serious low-mow",
      "Spike aerators of any kind: compresses soil sideways, makes compaction worse not better",
      "Manual plug aerators (foot-pump style): you will physically give up before doing meaningful coverage on anything larger than 500 sqft",
      "'Liquid aeration' as a sole solution: marketing pitch out ahead of the research data",
      "Ironite as your primary fertilizer: 1-0-1 NPK is essentially useless, buy iron separately and fertilizer separately",
      "Bagged 'topsoil' from big-box stores: often heavily diluted with wood fines and clay screenings, not real soil",
      "Probiotic teas, compost teas-in-a-bottle, 'soil biology shots': weak and inconsistent efficacy data on home lawns",
      "Generic 'all purpose' bagged seed: nearly always contains annual ryegrass nurse crop that dies in 12 months",
      "Pre-mixed ready-to-spray herbicide bottles (Spectracide, Ortho Weed B Gon, etc.): same actives as the concentrates at 5 to 10x the per-acre cost",
    ],
  },
  {
    id: "calendar",
    number: 14,
    title: "The 2026 product calendar at a glance",
    subtitle: "What to buy when, by season",
    intro:
      "A loose, region-agnostic timeline of when each product class earns its keep. Adjust 2 to 4 weeks based on your zone.",
  },
]

const COOL_SEASON_CALENDAR: { window: string; items: string[] }[] = [
  {
    window: "Late winter / early spring (Feb – Mar)",
    items: [
      "Pre-emergent app #1 (Barricade 4FL or Scotts Halts) before soil temps hit 55°F",
      "Soil test if you haven't this year",
      "Spreader calibration",
    ],
  },
  {
    window: "Mid spring (Mar – Apr)",
    items: [
      "First fertilizer app (Milorganite or PGF Complete)",
      "Spot-spray winter broadleaf with Tenacity or T-Zone SE",
      "First mow at proper height (3 inches+ for KBG, 3.5 inches+ for fescue)",
    ],
  },
  {
    window: "Late spring (May)",
    items: [
      "Iron foliar for color (PetraTools Liquid Iron)",
      "Crabgrass post-em if any sneak through (small tillers only)",
      "Skip nitrogen if entering summer heat",
    ],
  },
  {
    window: "Summer (Jun – Aug)",
    items: [
      "Wetting agent (Hydretain) for drought stress",
      "Mow tall (4 inches), water deep and infrequent",
      "No nitrogen, no broadleaf herbicides, no big projects",
    ],
  },
  {
    window: "Early fall (Aug – Sep)",
    items: [
      "Pre-emergent app #2 before soil temps fall through 70°F",
      "Core aeration + overseed (the prime cool-season window)",
      "Heavy fertilizer app (winterizer or PGF Complete)",
    ],
  },
  {
    window: "Late fall (Oct – Nov)",
    items: [
      "Final fertilizer / winterizer",
      "Final broadleaf spot-spray (cooler weather = better uptake on tough perennial weeds)",
      "Sharpen mower blade for next year",
    ],
  },
]

const WARM_SEASON_CALENDAR: { window: string; items: string[] }[] = [
  {
    window: "Late winter (Feb)",
    items: [
      "Pre-emergent app #1 (Barricade 4FL) before soil hits 55°F",
      "Soil test",
    ],
  },
  {
    window: "Spring green-up (Mar – Apr)",
    items: [
      "Spot-spray winter broadleaf with IMAGE Southern",
      "Light fertilizer once 50 percent green",
      "Scalp mow if you're managing low (bermuda / zoysia)",
    ],
  },
  {
    window: "Late spring / early summer (May – Jun)",
    items: [
      "Full fertilizer push (PGF Complete or Scotts Southern Lawn Food)",
      "Pre-emergent app #2 if first was 12 weeks ago",
      "Liquid iron for color",
    ],
  },
  {
    window: "Mid summer (Jul – Aug)",
    items: [
      "Continue fertilizer cadence (3 to 6 lbs N/yr for bermuda; less for St. Augustine and centipede)",
      "Wetting agent if drought stress",
      "Spot-spray summer broadleaf and grassy weeds",
    ],
  },
  {
    window: "Early fall (Sep)",
    items: [
      "Pre-emergent app #3 (Poa annua prevention)",
      "Light final nitrogen if you must, then taper",
      "Core aeration",
    ],
  },
  {
    window: "Late fall / winter (Oct – Jan)",
    items: [
      "Stop nitrogen 6 weeks before first frost",
      "Glyphosate winter weeds on dormant bermuda (only when fully dormant)",
      "Sharpen and service mower",
    ],
  },
]

// University extension and research sources we cross-check picks against.
// Every link is verified as live. Grouped by what a reader is actually trying
// to do: "diagnose first," "cool-season program," "warm-season program," and
// "organic / regional alternatives." Mirrors the canon you'd see in the
// r/lawncare sidebar — minus the dead links.
const TRUSTED_RESOURCES: {
  category: string
  description: string
  links: { name: string; href: string; blurb: string }[]
}[] = [
  {
    category: "Diagnose first",
    description:
      "Before you buy any product on this page, figure out what's actually wrong. These three diagnostic tools are how university extension agents triage homeowner questions.",
    links: [
      {
        name: "Purdue Turf Doctor",
        href: "https://www.entm.purdue.edu/turfdoctor/",
        blurb:
          "Photo-matched diagnosis for weeds, insects, diseases, and abiotic damage. The strongest single 'what's wrong with my lawn?' tool that exists.",
      },
      {
        name: "MSU Plant, Soil & Microbial Sciences — Weed Resources",
        href: "https://www.canr.msu.edu/weeds/extension/",
        blurb:
          "Michigan State's weed ID library and extension factsheets. (Replaces the old msuturfweeds.net, which is now a squatted domain.)",
      },
      {
        name: "UC IPM — Turf Weed Identification Key",
        href: "https://ipm.ucanr.edu/TOOLS/TURF/PESTS/weedkey.html",
        blurb:
          "Interactive weed key from UC Davis. Best Western-US counterpart to the MSU library.",
      },
    ],
  },
  {
    category: "Cool-season lawn programs",
    description:
      "Northern lawns (KBG, tall fescue, ryegrass, fine fescue). Establishment, fertilization, weed management, and renovation from the strongest cool-season turf programs in the country.",
    links: [
      {
        name: "Cornell Turfgrass Program — Lawn Care Step-by-Step",
        href: "https://turf.cals.cornell.edu/lawn/lawn-care-the-easiest-steps-to-an-attractive-environmental-asset/",
        blurb:
          "Northeast cool-season homeowner guide with month-by-month almanac. Cornell's whole turfgrass library lives at turf.cals.cornell.edu.",
      },
      {
        name: "Penn State Extension — Turfgrass and Lawn Care",
        href: "https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care",
        blurb:
          "Peter Landschoot's program. Best establishment and renovation publications for the Mid-Atlantic and lower Northeast.",
      },
      {
        name: "Rutgers Center for Turfgrass Science",
        href: "https://turf.rutgers.edu/",
        blurb:
          "Home of the NTEP cultivar trials that rank every reputable seed blend on the market.",
      },
      {
        name: "University of Minnesota Extension — Lawn Care",
        href: "https://extension.umn.edu/lawns-and-landscapes/lawn-care",
        blurb:
          "Northern cool-season care with a pollinator-friendly bent and clear nitrogen guidance.",
      },
    ],
  },
  {
    category: "Warm-season + transition zone",
    description:
      "Southern and transition lawns (bermuda, zoysia, St. Augustine, centipede, bahiagrass, tall fescue). Per-species calendars and fertilizer windows that vary by state.",
    links: [
      {
        name: "NC State Extension — Carolina Lawns",
        href: "https://content.ces.ncsu.edu/catalog/series/227/",
        blurb:
          "The authoritative transition-zone and tall-fescue/bermuda homeowner monograph.",
      },
      {
        name: "University of Georgia — Georgia Turf",
        href: "https://turf.caes.uga.edu/turfgrass-species.html",
        blurb:
          "Per-species maintenance calendars for bermuda, zoysia, centipede, St. Augustine, and tall fescue.",
      },
      {
        name: "UF/IFAS — Fertilizing Your Florida Lawn",
        href: "https://gardeningsolutions.ifas.ufl.edu/care/fertilizer/fertilizing-the-lawn/",
        blurb:
          "St. Augustine nitrogen windows, slow-release rates, and guidance on Florida fertilizer ordinances.",
      },
      {
        name: "Texas A&M AggieTurf",
        href: "https://aggieturf.tamu.edu/",
        blurb:
          "Texas turfgrass selection, weed ID, and insect management for all warm-season species.",
      },
    ],
  },
  {
    category: "Organic + regional alternatives",
    description:
      "Reduced-input pathways, native lawns, and regional climates that don't fit the cool-vs-warm dichotomy.",
    links: [
      {
        name: "Ohio State Extension — Natural Organic Lawn Care (HYG-4031)",
        href: "https://ohioline.osu.edu/factsheet/hyg-4031",
        blurb:
          "Practical organic transition factsheet. Cited across the no-synthetic community.",
      },
      {
        name: "Colorado State Extension — Buffalograss Lawns",
        href: "https://extension.colostate.edu/resource/buffalograss-lawns/",
        blurb:
          "The most credible single resource on native, low-water lawns. Essential reading west of the 100th meridian.",
      },
      {
        name: "Oregon State Extension — Practical Lawn Care for Western Oregon (EC 1521)",
        href: "https://extension.oregonstate.edu/catalog/ec-1521-practical-lawn-care-western-oregon",
        blurb:
          "The Pacific Northwest reference. Marine-climate fertilizer windows, moss management, and species selection.",
      },
    ],
  },
]

interface FormState {
  email: string
  isSubmitting: boolean
  isSubmitted: boolean
  errorMessage: string | null
  // Mailcheck-style "Did you mean ...?" hint surfaced under each capture
  // input. Computed on change; cleared when the user accepts the suggestion
  // or types a value that no longer triggers it.
  suggestion: string | null
}

export function LawnProductsGuideContent() {
  const [unlocked, setUnlocked] = useState(false)
  const [form, setForm] = useState<FormState>({
    email: "",
    isSubmitting: false,
    isSubmitted: false,
    errorMessage: null,
    suggestion: null,
  })
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle")
  const [readProgress, setReadProgress] = useState(0)
  // Two-step micro-commit: cool/warm pill choice in the hero card.
  // Doesn't gate the form — it just sticks the visitor to a season frame
  // before they hit the email field. Defaults to cool (covers KBG/fescue,
  // the larger slice of US lawns).
  const [seasonChoice, setSeasonChoice] = useState<"cool" | "warm">("cool")
  // Exit-intent modal — dismissable per session. Once dismissed, the user
  // gets a normal session without the popup re-firing.
  const [exitIntentDismissed, setExitIntentDismissed] = useState(false)
  const gatedRef = useRef<HTMLDivElement | null>(null)

  // Wire the exit-intent hook to gate the modal. Only enabled for non-unlocked
  // visitors who haven't dismissed yet. `useExitIntent` already triggers once
  // per session (it's stateful internally) so no extra de-dup needed.
  const { hasTriggered: exitIntentTriggered } = useExitIntent({
    enabled: !unlocked && !exitIntentDismissed,
    initialDelay: 10_000,
  })
  const showExitIntent = exitIntentTriggered && !exitIntentDismissed && !unlocked

  // Impression denominator for the capture funnel. One event per locked visit,
  // attached to the hero email card (above the fold, always the first gate
  // seen). Source matches the capture source's base so the PostHog
  // `email_capture_surface_shown` → `email_captured` funnel joins on
  // `surface_family` (products_guide); before 2026-07-17 this page logged
  // captures with no shown event at all.
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    "products_guide_2026",
    { placement: "hero" },
    { enabled: !unlocked },
  )

  // A/B test: form button copy. Mirror the homepage `hero-social-proof`
  // pattern. Default to control when flag is undefined (rollout-edge users
  // get the same experience as the control cohort).
  const ctaCopyVariant = useFeatureFlagVariantKey("lawn_guide_cta_copy_v1") as
    | "control"
    | "instant_access"
    | "email_picks"
    | undefined
  const submitLabel =
    ctaCopyVariant === "instant_access"
      ? "Get instant access (free)"
      : ctaCopyVariant === "email_picks"
        ? "Email me the 35 picks"
        : "Send me the picks"

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        if (window.localStorage.getItem(UNLOCK_KEY) === "1") {
          setUnlocked(true)
        }
        // Hydrate the email draft if the visitor started typing on a prior
        // visit. Catches the in-progress / navigated-away cohort.
        const draft = window.localStorage.getItem(EMAIL_DRAFT_KEY)
        if (draft && PATTERNS.EMAIL.test(draft)) {
          setForm((prev) => ({ ...prev, email: draft }))
        }
      }
    } catch {
      // localStorage can throw in privacy mode; failing closed is fine.
    }
  }, [])

  // Live reading-progress bar in the sticky brand bar. Tracks how far down
  // the document the visitor has scrolled. Passive listener so it doesn't
  // interfere with momentum scroll on iOS; no rAF throttle because the
  // state setter dedupes identical values.
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const max = doc.scrollHeight - doc.clientHeight
      const pct = max > 0 ? (doc.scrollTop / max) * 100 : 0
      setReadProgress(Math.min(100, Math.max(0, pct)))
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // suggestEmail is cheap (Levenshtein against a ~30-domain pool) so no
    // debounce — keeps the hint responsive as the user types past the @.
    const suggestion = suggestEmail(value)
    setForm((prev) =>
      prev.email === value && prev.suggestion === suggestion && prev.errorMessage === null
        ? prev
        : { ...prev, email: value, errorMessage: null, suggestion },
    )
    saveEmailDraft(value)
  }

  const applyEmailSuggestion = () => {
    setForm((prev) => {
      if (!prev.suggestion) return prev
      const next = prev.suggestion
      saveEmailDraft(next)
      // Don't ship raw email addresses to PostHog — match the newsletter
      // analytics pattern (lib/analytics.ts:599) and report only the
      // domains we typo-corrected from/to. The local-part isn't useful for
      // funnel analysis and is PII.
      trackEvent("email_typo_suggestion_accepted", {
        from_domain: prev.email.split('@')[1] ?? null,
        to_domain: next.split('@')[1] ?? null,
      })
      return {
        ...prev,
        email: next,
        suggestion: null,
        errorMessage: null,
      }
    })
  }

  const isEmailValid = PATTERNS.EMAIL.test(form.email.trim())

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const email = form.email.trim()
    if (!email || !PATTERNS.EMAIL.test(email)) {
      setForm((prev) => ({ ...prev, errorMessage: "Please enter a valid email address" }))
      return
    }

    setForm((prev) => ({ ...prev, isSubmitting: true, errorMessage: null }))

    try {
      const response = await fetch("/api/email/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          context: "products_guide_2026",
        }),
      })
      const data = await response.json().catch(() => ({}))

      if (!response.ok && response.status !== 409 && data.error !== "already_subscribed") {
        throw new Error(data.error || "Failed to subscribe")
      }

      const isReturning = response.status === 409 || data.error === "already_subscribed"
      // Encode the A/B variant into the source string so PostHog can split
      // conversion rate by variant without changing the analytics helper sig.
      const variantSuffix = ctaCopyVariant ? `__cta_${ctaCopyVariant}` : ""
      analytics.emailCaptured(
        (isReturning ? "products_guide_2026_returning" : "products_guide_2026") +
          variantSuffix,
      )

      try {
        window.localStorage.setItem(UNLOCK_KEY, "1")
        // Draft no longer needed once they're in.
        window.localStorage.removeItem(EMAIL_DRAFT_KEY)
      } catch {
        // Best-effort.
      }
      setUnlocked(true)
      setForm((prev) => ({ ...prev, isSubmitting: false, isSubmitted: true }))

      requestAnimationFrame(() => {
        gatedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    } catch (err) {
      setForm((prev) => ({
        ...prev,
        isSubmitting: false,
        errorMessage: err instanceof Error ? err.message : "Failed to subscribe",
      }))
    }
  }

  const handleShare = async (network: "twitter" | "facebook" | "pinterest" | "copy" | "native") => {
    const encodedUrl = encodeURIComponent(PAGE_URL)
    const encodedText = encodeURIComponent(`${SHARE_TITLE} — ${SHARE_TEXT}`)

    if (network === "native" && typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url: PAGE_URL })
        return
      } catch {
        // user cancelled
        return
      }
    }

    if (network === "copy") {
      try {
        await navigator.clipboard.writeText(PAGE_URL)
        setCopyState("copied")
        setTimeout(() => setCopyState("idle"), 2000)
      } catch {
        // fail silently
      }
      return
    }

    let shareUrl = ""
    if (network === "twitter") {
      shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`
    } else if (network === "facebook") {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    } else if (network === "pinterest") {
      const pinImg = encodeURIComponent(absoluteBrandAssetUrl(BRAND_ASSET_PATHS.openGraph))
      shareUrl = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${pinImg}&description=${encodedText}`
    }

    if (shareUrl && typeof window !== "undefined") {
      window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=500")
    }
  }

  const handleAffiliateClick = (pick: ProductPick) => {
    contentAnalytics.affiliateLinkClicked(
      "lawn-products-guide-2026",
      pick.name,
      pick.amazon,
      "inline",
    )
    contentAnalytics.affiliateProductClicked("inline", {
      productName: pick.name,
      affiliateLink: pick.amazon,
      article_slug: "lawn-products-guide-2026",
    })
  }

  const previewSections = SECTIONS.filter((s) => s.preview)
  const gatedSections = SECTIONS.filter((s) => !s.preview)

  const captureProps = {
    email: form.email,
    onChange: handleEmailChange,
    onSubmit: handleSubmit,
    isSubmitting: form.isSubmitting,
    suggestion: form.suggestion,
    onApplySuggestion: applyEmailSuggestion,
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky reading-progress bar — pct, current section name, inline
          "Email me the picks" CTA that jumps to the hero form. Mobile shows
          a thin hairline only; desktop gets the full bar. */}
      <div className="hidden md:flex sticky top-[70px] z-40 items-center gap-4 px-6 lg:top-[78px] lg:px-10 py-2.5 bg-white/95 backdrop-blur border-b border-gray-200 print:hidden">
        <div className="flex items-center gap-2.5 flex-1">
          <div className="font-mono text-[12px] font-bold text-gray-500 tabular-nums">
            {Math.round(readProgress)}%
          </div>
          <div className="flex-1 h-[3px] bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 rounded-full transition-[width] duration-150 ease-out"
              style={{ width: `${readProgress}%` }}
            />
          </div>
        </div>
        {!unlocked && (
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("hero-email") as HTMLInputElement | null
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" })
                window.setTimeout(() => el.focus(), 500)
              }
            }}
            className="inline-flex min-h-11 items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-700"
          >
            Email me the picks
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
        )}
      </div>

      {/* Mobile hairline progress bar */}
      <div className="md:hidden fixed top-[70px] left-0 right-0 z-40 h-0.5 print:hidden pointer-events-none">
        <div
          className="h-full bg-green-600 transition-[width] duration-150 ease-out"
          style={{ width: `${readProgress}%` }}
          aria-hidden="true"
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-12 md:pb-16">
        <Reveal onMount delay={0.02} y={6}>
          <BreadcrumbComponent
            items={[
              { label: "Home", href: "/" },
              { label: "Guides", href: "/guides" },
              { label: "2026 Products Guide" },
            ]}
          />
        </Reveal>

        {/* Hero — 2-column grid: copy left, dominant email card right (1.1fr).
            On mobile, stacks vertically with the email card after the headline. */}
        <header className="mt-6 mb-10 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
          {/* Left — copy. Cascades in on first paint via Stagger.onMount. */}
          <Stagger onMount staggerChildren={0.07} delayChildren={0.04}>
            <Stagger.Item className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 px-3 py-1 rounded-full text-[12px] font-semibold mb-4">
              <GrassTuft className="w-3.5 h-3.5" strokeWidth={2.4} />
              2026 buyer's guide · always free
            </Stagger.Item>
            <Stagger.Item>
              <h1 className="text-[32px] sm:text-5xl lg:text-[56px] font-extrabold text-gray-900 leading-[1.22] tracking-tight text-balance">
                The 35 lawn products{" "}
                <span className="inline-block whitespace-nowrap text-green-700 underline decoration-green-600 decoration-[4px] underline-offset-[6px]">
                  worth your money
                </span>{" "}
                in 2026
              </h1>
            </Stagger.Item>
            <Stagger.Item>
              <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl text-pretty">
                And the 12 to skip. Real picks for cool-season and warm-season
                lawns. No affiliate listicles, no sponsored top-tens.
              </p>
            </Stagger.Item>

            {/* Stat strip — 4 columns, hairline dividers.
                Mobile (2-col): vertical divider between cols 1|2, horizontal
                divider between rows. sm+ (4-col): vertical between every cell.
                Numeric stats use CountUp for a polished on-mount tick. */}
            <Stagger.Item className="mt-8 grid grid-cols-2 sm:grid-cols-4 py-5 border-t border-b border-gray-200">
              {(
                [
                  { v: 35, l: "editor's picks", isCount: true },
                  { v: 12, l: "to skip", isCount: true },
                  { v: "12 min", l: "read", isCount: false },
                  { v: "$80–200*", l: "saved / year", isCount: false },
                ] as const
              ).map((s, i) => (
                <div
                  key={i}
                  className={cn(
                    "px-4 sm:px-5",
                    // Mobile: cells in the left column (i=0, i=2) sit flush with the
                    // headline/subtitle above. sm+ restores normal cell padding.
                    i === 0 && "pl-0",
                    i === 2 && "pl-0 sm:pl-5",
                    (i === 1 || i === 3) && "border-l border-gray-200",
                    i === 2 && "sm:border-l sm:border-gray-200",
                    i >= 2 && "border-t border-gray-200 pt-4 sm:border-t-0 sm:pt-0",
                  )}
                >
                  <div className="text-[22px] sm:text-2xl font-bold text-gray-900 tracking-tight tabular-nums whitespace-nowrap">
                    {s.isCount ? (
                      <CountUp value={s.v as number} onMount duration={0.7} />
                    ) : (
                      s.v
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500">
                    {s.l}
                  </div>
                </div>
              ))}
            </Stagger.Item>

            <Stagger.Item className="mt-2 text-[11px] text-gray-500 leading-relaxed">
              *Savings estimate for a 5,000 sqft lawn switching from a typical Scotts 4-Step + RTS herbicide program (~$240/yr) to a concentrate-based DIY program (~$100/yr). Bigger lawn = bigger gap.
            </Stagger.Item>

            {/* 3-bullet teaser */}
            <Stagger.Item>
              <ul className="mt-6 grid gap-2.5">
                {[
                  "The $30 pre-emergent that ends crabgrass for the season",
                  "The fertilizer that builds color without burning out",
                  "The 12 products to skip. Most homeowners are buying 3 right now",
                ].map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[15px] text-gray-800"
                  >
                    <span className="flex-shrink-0 mt-0.5 w-[22px] h-[22px] rounded-full bg-green-50 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-green-700" strokeWidth={2.5} />
                    </span>
                    <span className="text-pretty">{t}</span>
                  </li>
                ))}
              </ul>
            </Stagger.Item>
          </Stagger>

          {/* Right — dominant email card. Eases in slightly after the copy. */}
          <Reveal onMount delay={0.08} y={12}>
            {!unlocked ? (
              <div ref={impressionRef} className="ds-glass rounded-2xl p-6 sm:p-8">
                <Stagger staggerChildren={0.05} delayChildren={0.1}>
                {/* Social proof ribbon */}
                <Stagger.Item className="flex items-center gap-3 pb-4 mb-4 border-b border-dashed border-gray-200">
                  <div className="flex">
                    {[
                      { bg: "bg-orange-200", initial: "B" },
                      { bg: "bg-green-200", initial: "S" },
                      { bg: "bg-blue-200", initial: "M" },
                      { bg: "bg-amber-200", initial: "J" },
                    ].map((a, i) => (
                      <div
                        key={i}
                        className={cn(
                          "w-[26px] h-[26px] rounded-full border-2 border-white grid place-items-center text-[10px] font-bold text-gray-700",
                          a.bg,
                          i !== 0 && "-ml-2",
                        )}
                        aria-hidden="true"
                      >
                        {a.initial}
                      </div>
                    ))}
                  </div>
                  <div className="text-[13px] text-gray-700 leading-snug text-pretty">
                    <strong className="font-bold text-gray-900">1k+ lawn lovers</strong>{" "}
                    got this last month
                  </div>
                </Stagger.Item>

                <Stagger.Item className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-green-50 grid place-items-center">
                    <Mail className="w-5 h-5 text-green-700" strokeWidth={2.2} />
                  </div>
                  <div className="text-[11.5px] font-bold uppercase tracking-[0.08em] text-green-700">
                    Email me the picks
                  </div>
                </Stagger.Item>
                <Stagger.Item className="text-[22px] sm:text-[26px] font-extrabold text-gray-900 leading-[1.2] tracking-tight mb-5 text-balance">
                  Unlock all 35 picks + the 12 to skip
                </Stagger.Item>

                <Stagger.Item>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Step 1 — cool/warm micro-commit */}
                  <div>
                    <div className="text-[12px] font-bold uppercase tracking-[0.06em] text-gray-500 mb-2">
                      Step 1 · your grass
                    </div>
                    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Grass season">
                      {(
                        [
                          { key: "cool", label: "Cool-season", Icon: Snowflake },
                          { key: "warm", label: "Warm-season", Icon: Sun },
                        ] as const
                      ).map(({ key, label, Icon }) => {
                        const active = seasonChoice === key
                        return (
                          <motion.button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => setSeasonChoice(key)}
                            whileTap={{ scale: 0.97 }}
                            transition={FADE_TRANSITION}
                            className={cn(
                              "flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-colors",
                              active
                                ? key === "cool"
                                  ? "bg-teal-50 border-teal-600 text-teal-700"
                                  : "bg-amber-50 border-amber-600 text-amber-700"
                                : "bg-white/55 backdrop-blur-md border-white/55 text-gray-700 hover:bg-white/75 hover:border-white/85",
                            )}
                          >
                            <Icon className="w-4 h-4" strokeWidth={2} />
                            {label}
                          </motion.button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Step 2 — email */}
                  <div>
                    <label htmlFor="hero-email" className="block text-[12px] font-bold uppercase tracking-[0.06em] text-gray-500 mb-2">
                      Step 2 · your email
                    </label>
                    <div className="relative">
                      <Input
                        id="hero-email"
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleEmailChange}
                        className={cn(
                          "w-full px-4 py-3 h-12 text-base rounded-lg border-[1.5px] focus:ring-0 focus:outline-hidden transition-colors",
                          isEmailValid
                            ? "border-green-500 focus:border-green-600 pr-10"
                            : "border-gray-300 focus:border-green-500",
                        )}
                        style={{ boxShadow: "none" }}
                        aria-invalid={form.email.length > 0 && !isEmailValid}
                      />
                      {isEmailValid && !form.suggestion && (
                        <CheckCircle2
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-50"
                          aria-hidden="true"
                          strokeWidth={2.5}
                        />
                      )}
                    </div>
                    {form.suggestion && (
                      <EmailSuggestionHint
                        suggestion={form.suggestion}
                        onApply={applyEmailSuggestion}
                      />
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={form.isSubmitting}
                    className="group w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-[15px] shadow-md shadow-green-500/25"
                  >
                    {form.isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Unlocking...
                      </>
                    ) : (
                      <>
                        {submitLabel}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>

                  {form.errorMessage && (
                    <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>{form.errorMessage}</span>
                    </div>
                  )}
                </form>
                </Stagger.Item>

                {/* Trust lines */}
                <Stagger.Item className="mt-4 grid gap-2">
                  {[
                    { Icon: CheckCircle2, t: "No spam, one-click unsubscribe" },
                    { Icon: Clock, t: "10-second signup, instant delivery" },
                    { Icon: Star, t: "Editor's picks, not affiliate rankings" },
                  ].map(({ Icon, t }) => (
                    <div key={t} className="flex items-center gap-2 text-[13px] text-gray-600">
                      <Icon className="w-3.5 h-3.5 text-green-600 flex-shrink-0" strokeWidth={2} />
                      {t}
                    </div>
                  ))}
                </Stagger.Item>

                {/* Affiliate disclosure */}
                <Stagger.Item className="mt-5 pt-4 border-t border-dashed border-gray-200 text-[11.5px] text-gray-500 leading-relaxed">
                  <Lock className="inline-block w-3 h-3 text-gray-400 align-[-1px] mr-1" strokeWidth={2} />
                  Affiliate links inside. We earn on Amazon purchases at no cost to you.
                  No paid placements, picks are editorial.
                </Stagger.Item>
                </Stagger>
              </div>
            ) : (
              <Card
                className={cn(
                  "border-green-300 bg-green-50/70 print:hidden",
                  form.isSubmitted &&
                    "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-500",
                )}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div className="relative flex-shrink-0">
                      <CheckCircle2
                        className={cn(
                          "w-6 h-6 text-green-600 mt-0.5",
                          form.isSubmitted &&
                            "motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-700",
                        )}
                      />
                      {form.isSubmitted && (
                        <span
                          aria-hidden="true"
                          className="absolute inset-0 rounded-full bg-green-400/30 motion-safe:animate-ping"
                          style={{ animationIterationCount: 2, animationDuration: "900ms" }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-green-900">
                        {form.isSubmitted
                          ? "You're in. The full guide is unlocked below."
                          : "Welcome back, the full guide is unlocked."}
                      </p>
                      <p className="text-sm text-green-800 mt-0.5">
                        Bookmark this page or save a copy for later.
                      </p>
                    </div>
                    <Bookmark className="w-5 h-5 text-green-600 hidden sm:block mt-0.5" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <PdfExportButton size="sm" label="Save as PDF" />
                    <Button
                      type="button"
                      onClick={() => handleShare("copy")}
                      variant="outline"
                      size="sm"
                      className="border-green-300 text-green-700 hover:bg-green-100 bg-white"
                    >
                      {copyState === "copied" ? (
                        <>
                          <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 mr-1.5" />
                          Copy link
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </Reveal>
        </header>

        {/* Credibility strip — author + verification + price-check date.
            Sits directly below the hero to anchor trust before any picks. */}
        <Reveal y={8}>
        <section className="mb-10 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-5 border-y border-white/45 bg-white/45 backdrop-blur-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <Image
              src="/images/authors/james-thornton.webp"
              alt="James Thornton"
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover ring-1 ring-gray-200 flex-shrink-0"
            />
            <div>
              <div className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                Reviewed by
              </div>
              <div className="mt-0.5 text-[15px] text-gray-700">
                <Link
                  href="/blog/author/james-thornton"
                  className="font-bold text-gray-900 hover:text-green-700 hover:underline underline-offset-2"
                >
                  James Thornton
                </Link>
                <span className="text-gray-500 font-normal">
                  {" "}
                  · Lawn equipment & maintenance, 20 years
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-2 text-[13px] text-gray-700">
              <CheckCircle2 className="w-4 h-4 text-green-600" strokeWidth={2} />
              <span>
                <strong className="text-gray-900 font-semibold">Verified</strong> May 10, 2026
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-gray-700">
              <Clock className="w-4 h-4 text-gray-500" strokeWidth={2} />
              12 min read
            </div>
          </div>
        </section>
        </Reveal>

        {/* "How we picked" — 4-tile band. The biggest trust differentiator vs
            the SEO listicle hellscape: tell the reader exactly how the picks
            were chosen, then they don't have to wonder. */}
        <section className="mb-12">
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" staggerChildren={0.05}>
            {[
              {
                Icon: Leaf,
                t: "Tested or used",
                d: "On lawns our team manages, not vendor demos.",
              },
              {
                Icon: CheckCircle2,
                t: "NTEP-backed",
                d: "Named cultivars with replicated trial data.",
              },
              {
                Icon: FlaskConical,
                t: "Real prices",
                d: "Spot-checked on Amazon each update.",
              },
              {
                Icon: Lock,
                t: "No paid spots",
                d: "Rank cannot be bought. Ever.",
              },
            ].map(({ Icon, t, d }) => (
              <Stagger.Item key={t}>
                <HoverLift
                  lift={-2}
                  pressScale={0.995}
                  className="ds-glass h-full rounded-xl p-4 sm:p-5 transition-shadow"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-50 grid place-items-center mb-3">
                    <Icon className="w-[18px] h-[18px] text-green-700" strokeWidth={2.2} />
                  </div>
                  <div className="text-[14.5px] font-bold text-gray-900 tracking-tight">
                    {t}
                  </div>
                  <p className="mt-1 text-[13px] text-gray-600 leading-relaxed">{d}</p>
                </HoverLift>
              </Stagger.Item>
            ))}
          </Stagger>
          <Reveal y={6} delay={0.12}>
            <p className="mt-3 text-xs text-gray-500 leading-relaxed">
              Cross-checked against extension research from Cornell, Penn State,
              Purdue, Rutgers, UMN, NC State, UF, UGA, Texas A&amp;M, OSU, and CSU.
              Sources linked in{" "}
              <a
                href="#trusted-resources"
                className="text-green-700 underline-offset-2 hover:underline font-semibold"
              >
                Trusted resources
              </a>
              .
            </p>
          </Reveal>
        </section>

        {/* At-a-glance — 6 cards in a 3-col grid. Locked picks blur the
            name with a "Tap to unlock" chip so they're visibly there to
            be unlocked. */}
        <section className="mb-12">
          <Reveal y={8}>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-5">
              <div>
                <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-green-700">
                  At a glance
                </div>
                <h2 className="mt-1 text-[28px] sm:text-[32px] font-extrabold text-gray-900 tracking-tight">
                  Our top picks
                </h2>
              </div>
              <div className="text-[13px] text-gray-500">
                Full picks (budget / best value / pro) in each section below.
              </div>
            </div>
          </Reveal>

          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5" staggerChildren={0.05}>
            {HERO_PICK_REFS.map((ref) => {
              const section = SECTIONS.find((s) => s.id === ref.sectionId)
              const pick = section?.picks?.[ref.pickIndex]
              if (!section || !pick) return null
              const locked = !unlocked && !section.preview
              return (
                <Stagger.Item key={ref.sectionId} className="h-full">
                  <HeroPick
                    pick={pick}
                    label={ref.label}
                    sectionId={ref.sectionId}
                    tone={ref.tone}
                    locked={locked}
                    onUnlock={() => {
                      const el = document.getElementById("hero-email") as HTMLInputElement | null
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "center" })
                        window.setTimeout(() => el.focus(), 500)
                      }
                    }}
                  />
                </Stagger.Item>
              )
            })}
          </Stagger>
        </section>

        {/* TOC — 14 sections in a 2-col grid. Each row: monospace number,
            section-icon tile, title, 1-line verdict, lock state.
            Heading + chip-row fade in together; rows cascade via Stagger. */}
        <section className="mb-12 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-8 sm:py-10 bg-white/45 backdrop-blur-md border-y border-white/45">
          <Reveal y={8}>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-5">
              <h2 className="text-[22px] font-bold text-gray-900 tracking-tight">
                What's inside{" "}
                <span className="text-gray-500 font-medium">· 14 sections</span>
              </h2>
              <div className="flex gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm bg-green-600" />
                  Free preview
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="w-3 h-3 text-gray-400" strokeWidth={2} />
                  Unlocks with email
                </span>
              </div>
            </div>
          </Reveal>
          <Stagger role="list" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
            {[...SECTIONS]
              .sort((a, b) => a.number - b.number)
              .map((s) => {
                const isLocked = !unlocked && !s.preview
                const SectionIcon = SECTION_ICON_BY_ID[s.id] ?? Leaf
                return (
                  <Stagger.Item
                    key={s.id}
                    role="listitem"
                    className="border-b border-gray-200 last:border-b-0 md:last-of-type:border-b-0"
                  >
                    <a
                      href={`#${s.id}`}
                      className="grid grid-cols-[34px_28px_1fr_auto] items-center gap-3 py-3 group"
                    >
                      <span
                        className={cn(
                          "font-mono text-[11.5px] font-bold tracking-[0.04em]",
                          isLocked ? "text-gray-400" : "text-green-700",
                        )}
                      >
                        {String(s.number).padStart(2, "0")}
                      </span>
                      <span
                        className={cn(
                          "w-7 h-7 rounded-lg border grid place-items-center flex-shrink-0",
                          isLocked
                            ? "bg-white/55 backdrop-blur-md border-white/55"
                            : "bg-green-50 border-green-100",
                        )}
                      >
                        <SectionIcon
                          className={cn(
                            "w-[15px] h-[15px]",
                            isLocked ? "text-gray-400" : "text-green-700",
                          )}
                          strokeWidth={2}
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14.5px] font-semibold text-gray-900 group-hover:text-green-700 leading-tight transition-colors tracking-tight">
                          {s.title}
                        </span>
                        <span className="block text-[12.5px] text-gray-500 mt-0.5 truncate">
                          {s.verdict || s.subtitle}
                        </span>
                      </span>
                      {isLocked ? (
                        <Lock className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 border border-green-100 text-[10.5px] font-bold uppercase tracking-wider text-green-700">
                          Preview
                        </span>
                      )}
                    </a>
                  </Stagger.Item>
                )
              })}
          </Stagger>
        </section>

        {/* Preview sections (always visible) — each fades in as it enters view.
            InlineCapture at the end of every preview section catches scrollers
            who got value but haven't yet committed. */}
        <div className="space-y-16">
          {previewSections.map((section) => (
            <Section
              key={section.id}
              section={section}
              onAffiliateClick={handleAffiliateClick}
              capture={!unlocked ? captureProps : undefined}
            />
          ))}
        </div>

        {/* Mid-page email gate — split card. Left: pitch + chip-grid of
            every locked category. Right: form on a soft green wash. */}
        {!unlocked && (
          <div ref={gatedRef} className="mt-16">
            <Reveal soft y={16}>
            <div className="ds-glass rounded-[20px] overflow-hidden grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
              {/* Left — pitch */}
              <div className="p-8 lg:p-10">
                <Reveal y={6} delay={0.1}>
                  <div className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 px-3 py-1 rounded-full text-[12px] font-semibold mb-4">
                    <Lock className="w-3.5 h-3.5" strokeWidth={2.2} />
                    Liking these picks? 12 more sections.
                  </div>
                </Reveal>
                <Reveal y={8} delay={0.16}>
                  <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] font-extrabold text-gray-900 leading-[1.1] tracking-tight">
                    25 more picks behind{" "}
                    <span className="text-green-700 border-b-[3px] border-green-600 pb-0.5">
                      one click
                    </span>
                  </h2>
                </Reveal>
                <Reveal y={8} delay={0.22}>
                  <p className="mt-4 mb-5 text-[15.5px] text-gray-600 leading-relaxed">
                    You haven't seen Barricade 4FL (the best dollar in lawn care),
                    SOLO 425, MySoil, EGO LM2156SP, PetraTools Iron, Rachio 3,
                    Hydretain, NightShift seed, GrubEx — plus the full 2026
                    calendar and the 12 to skip.
                  </p>
                </Reveal>
                <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" staggerChildren={0.03}>
                  {(
                    [
                      ["SprayCan", "Pre-emergent"],
                      ["SprayCan", "Sprayers"],
                      ["FlaskConical", "Soil tests"],
                      ["Leaf", "Spreaders"],
                      ["Sprout", "Seed brands"],
                      ["Droplets", "Iron · color"],
                      ["Scissors", "Mowers"],
                      ["Leaf", "Aeration"],
                      ["Droplets", "Smart irrigation"],
                      ["Droplets", "Wetting agents"],
                      ["XCircle", "What NOT to buy"],
                      ["Clock", "2026 calendar"],
                    ] as const
                  ).map(([iconName, label]) => {
                    const IconMap: Record<string, LucideIcon> = {
                      SprayCan,
                      FlaskConical,
                      Leaf,
                      Sprout,
                      Droplets,
                      Scissors,
                      XCircle,
                      Clock,
                    }
                    const ChipIcon = IconMap[iconName] ?? Leaf
                    return (
                      <Stagger.Item
                        key={`${iconName}-${label}`}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/55 backdrop-blur-md border border-white/55"
                      >
                        <ChipIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" strokeWidth={2} />
                        <span className="text-[12px] text-gray-700 font-medium truncate">
                          {label}
                        </span>
                      </Stagger.Item>
                    )
                  })}
                </Stagger>
              </div>

              {/* Right — form on soft green wash */}
              <div className="bg-linear-to-b from-green-50 to-white p-8 lg:p-10 border-t lg:border-t-0 lg:border-l border-white/45 flex flex-col justify-center">
                <form onSubmit={handleSubmit}>
                  <Stagger staggerChildren={0.05} delayChildren={0.18}>
                    <Stagger.Item>
                      <label
                        htmlFor="gate-email"
                        className="block text-[13px] font-semibold text-gray-700 mb-2"
                      >
                        Email address
                      </label>
                      <Input
                        id="gate-email"
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={handleEmailChange}
                        className="w-full h-12 px-4 text-base rounded-lg border-[1.5px] border-gray-300 bg-white focus:border-green-500 focus:ring-0"
                        style={{ boxShadow: "none" }}
                      />
                      {form.suggestion && (
                        <EmailSuggestionHint
                          suggestion={form.suggestion}
                          onApply={applyEmailSuggestion}
                        />
                      )}
                    </Stagger.Item>
                    <Stagger.Item>
                      <Button
                        type="submit"
                        disabled={form.isSubmitting}
                        className="mt-3 w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-[15px] shadow-md shadow-green-500/25"
                      >
                        {form.isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Unlocking...
                          </>
                        ) : (
                          <>
                            Send me the picks
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </Stagger.Item>
                    <Stagger.Item className="mt-3.5 flex justify-between text-xs text-gray-500">
                      <span>Free. No credit card.</span>
                      <span>One-click unsubscribe.</span>
                    </Stagger.Item>
                  </Stagger>
                  {form.errorMessage && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      <span>{form.errorMessage}</span>
                    </div>
                  )}
                </form>
              </div>
            </div>
            </Reveal>
          </div>
        )}

        {/* Gated sections — Medium-style fade-to-white. The premium pattern
            (Medium, NYT, Bloomberg, Substack all variants) is NOT blur —
            blur leaves text as "almost readable fuzzy shapes" which reads
            amateur. The professional pattern is a linear-gradient overlay
            from transparent to the page background color, sitting on top
            of fully-opaque content. The user sees the start of section 3
            crisp and readable, then it gradually dissolves into the page
            background. No janky blur, no smudge — it just becomes the page.

            Container is truncated with `max-height` so we only render one
            viewport-tall teaser visually. Overlay is `absolute inset-x-0
            bottom-0` covering the bottom 75% of the teaser, fading from
            transparent → white. Background-color of the wrapping page is
            white in this section so the gradient terminates cleanly. */}
        {/* .guide-gated-content is referenced by the Article JSON-LD hasPart
            cssSelector in layout.tsx (paywalled-content markup). */}
        <div className="guide-gated-content relative mt-16" style={!unlocked ? { maxHeight: "22rem", overflow: "hidden" } : undefined}>
          <div
            className={cn("space-y-16", !unlocked && "pointer-events-none select-none")}
            aria-hidden={!unlocked}
          >
            {gatedSections.map((section) => (
              <div key={section.id}>
                <Section section={section} onAffiliateClick={handleAffiliateClick} />
                {section.id === "calendar" && (
                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <CalendarLane
                      season="cool"
                      title="Cool-season"
                      subtitle="KBG · fescue · ryegrass"
                      entries={COOL_SEASON_CALENDAR}
                    />
                    <CalendarLane
                      season="warm"
                      title="Warm-season"
                      subtitle="Bermuda · zoysia · St. Augustine"
                      entries={WARM_SEASON_CALENDAR}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {!unlocked && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0"
              style={{
                height: "75%",
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,1) 100%)",
              }}
            />
          )}
        </div>

        {/* Trusted Resources — university extension links the picks are
            cross-checked against. Always visible (outside the gated wrapper)
            because authority signals help every reader, not just subscribers. */}
        <TrustedResources />

        {/* Share strip — horizontal card with copy left and share buttons right */}
        <section className="mt-16 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-10 bg-white/45 backdrop-blur-md border-t border-white/45">
          <Reveal y={10}>
          <div className="ds-glass rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                Found this useful?
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Send it to a friend who's still buying Scotts 4-Step.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleShare("twitter")}
                className="inline-flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-lg border border-gray-300 hover:border-green-400 hover:bg-green-50 text-sm font-semibold text-gray-900 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share on X
              </button>
              <button
                type="button"
                onClick={() => handleShare("facebook")}
                className="inline-flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-lg border border-gray-300 hover:border-green-400 hover:bg-green-50 text-sm font-semibold text-gray-900 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <Share2 className="w-3.5 h-3.5" />
                Facebook
              </button>
              <button
                type="button"
                onClick={() => handleShare("copy")}
                className="inline-flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-lg border border-gray-300 hover:border-green-400 hover:bg-green-50 text-sm font-semibold text-gray-900 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                {copyState === "copied" ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy link
                  </>
                )}
              </button>
              {unlocked && (
                <PdfExportButton
                  size="sm"
                  label="Save as PDF"
                  className="min-h-11 rounded-lg border border-gray-300 hover:border-green-400 hover:bg-green-50 text-gray-900"
                />
              )}
            </div>
          </div>
          </Reveal>
        </section>

        {/* Next-steps band — 4 calculator tiles */}
        <section className="mt-12 mb-8">
          <Reveal y={8}>
            <div className="mb-6">
              <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-green-700">
                Next steps for your lawn
              </div>
              <h2 className="mt-1 text-[26px] sm:text-[28px] font-extrabold text-gray-900 tracking-tight leading-tight">
                The guide tells you what to buy. The site tells you how much.
              </h2>
              <p className="mt-2 text-[15px] text-gray-500">
                Run the calculators with your actual lawn size and grass type for
                exact rates and timing.
              </p>
            </div>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" staggerChildren={0.05}>
            {[
              {
                href: "/tools/fertilizer-calculator",
                t: "Fertilizer calculator",
                d: "Exact rate per 1,000 sqft for your bag and grass.",
                Icon: Leaf,
              },
              {
                href: "/tools/soil-temperature",
                t: "Pre-emergent timing",
                d: "Live soil temperature for your ZIP.",
                Icon: FlaskConical,
              },
              {
                href: "/tools",
                t: "All free tools",
                d: "Lawn size, watering, mowing height, more.",
                Icon: CheckCircle2,
              },
              {
                href: "/products",
                t: "Browse products",
                d: "Every pick, filterable by grass and season.",
                Icon: GrassTuft,
              },
            ].map(({ href, t, d, Icon }) => (
              <Stagger.Item key={t}>
                <HoverLift lift={-2} pressScale={0.995} className="h-full rounded-xl">
                  <Link
                    href={href}
                    className="group block h-full ds-glass rounded-xl p-4 hover:border-green-300 hover:shadow-sm transition-all"
                  >
                    <div className="w-8 h-8 rounded-lg bg-green-50 grid place-items-center mb-3">
                      <Icon className="w-[17px] h-[17px] text-green-700" strokeWidth={2.2} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[14.5px] font-bold text-gray-900 tracking-tight">
                        {t}
                      </div>
                      <ArrowRight
                        className="w-[15px] h-[15px] text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-green-600"
                        strokeWidth={2}
                      />
                    </div>
                    <p className="mt-1 text-[12.5px] text-gray-500 leading-relaxed">
                      {d}
                    </p>
                  </Link>
                </HoverLift>
              </Stagger.Item>
            ))}
          </Stagger>
        </section>

        {/* FTC disclosure tucked at the bottom — clear and conspicuous for
            compliance without scaring visitors off above the fold. */}
        <Reveal y={6}>
          <div className="mt-8 mb-4 text-xs text-gray-500 leading-relaxed italic px-1">
            <strong className="not-italic text-gray-600">Affiliate disclosure:</strong>{" "}
            What Grass Is This? is a participant in the Amazon Services LLC Associates
            Program. We may earn commission when you buy through our links, at no extra
            cost to you. We only feature products we'd put on our own lawn. Prices
            shown were accurate at last verification and can change.
          </div>
        </Reveal>
      </div>

      {/* Exit-intent modal — fires once per session on mouse-leave-toward-top
          (desktop) or 90% scroll depth (mobile). Dismissing it stores in
          session-local state so it doesn't re-fire after the user closes it. */}
      {showExitIntent && (
        <ExitIntentModal
          email={form.email}
          isSubmitting={form.isSubmitting}
          errorMessage={form.errorMessage}
          suggestion={form.suggestion}
          onChange={handleEmailChange}
          onSubmit={handleSubmit}
          onApplySuggestion={applyEmailSuggestion}
          onClose={() => setExitIntentDismissed(true)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Exit-intent modal — fires once per session for non-unlocked visitors
// when they signal they're about to leave. Matches the "Wait — before
// you go" artboard from the design.
function ExitIntentModal({
  email,
  isSubmitting,
  errorMessage,
  suggestion,
  onChange,
  onSubmit,
  onApplySuggestion,
  onClose,
}: {
  email: string
  isSubmitting: boolean
  errorMessage: string | null
  suggestion: string | null
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: (e?: React.FormEvent) => void
  onApplySuggestion: () => void
  onClose: () => void
}) {
  // Lock body scroll while the modal is open so users can't scroll past it
  // on mobile, and so desktop mouse-leave-then-back doesn't re-fire chatter.
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = original
    }
  }, [])

  // Escape key dismisses
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <MotionConfig reducedMotion="user">
    <motion.div
      className="fixed inset-0 z-[70] grid place-items-center bg-gray-900/55 backdrop-blur-[2px] p-4 sm:p-6 print:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={FADE_TRANSITION}
      onClick={(e) => {
        // Click outside dismisses; click inside the card doesn't bubble
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <motion.div
        className="relative w-full max-w-[560px] bg-white rounded-2xl border border-gray-200 shadow-[0_40px_80px_rgba(0,0,0,0.3)] p-6 sm:p-9"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={FADE_TRANSITION_SOFT}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200 grid place-items-center transition-colors"
        >
          <X className="w-4 h-4 text-gray-600" strokeWidth={2} />
        </button>

        <div className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 px-3 py-1 rounded-full text-[12px] font-semibold mb-4">
          <Mail className="w-3.5 h-3.5" strokeWidth={2.2} />
          Wait — before you go
        </div>
        <h2
          id="exit-intent-title"
          className="text-[26px] sm:text-[30px] font-extrabold text-gray-900 leading-[1.1] tracking-tight"
        >
          Don't leave without the picks
        </h2>
        <p className="mt-3 mb-5 text-[15px] text-gray-600 leading-relaxed">
          The full 35-product list lands in your inbox in under a minute.
          One email, no follow-ups unless you want them.
        </p>

        <form onSubmit={onSubmit}>
          <Input
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={onChange}
            className="w-full h-12 px-4 text-base rounded-lg border-[1.5px] border-gray-300 bg-white focus:border-green-500 focus:ring-0"
            style={{ boxShadow: "none" }}
            aria-label="Email address"
            autoFocus
          />
          {suggestion && (
            <EmailSuggestionHint
              suggestion={suggestion}
              onApply={onApplySuggestion}
            />
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="mt-3 w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-[15px] shadow-md shadow-green-500/25"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send me the 35 picks
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          {errorMessage && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}
        </form>

        <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Lock className="w-3 h-3 text-gray-400" strokeWidth={2} />
            No spam
          </span>
          <span className="inline-flex items-center gap-1">
            <Check className="w-3 h-3 text-gray-400" strokeWidth={2} />
            One-click unsubscribe
          </span>
          <span className="inline-flex items-center gap-1">
            1k+ readers
          </span>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="block mx-auto mt-5 text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
        >
          No thanks, I'll keep buying Scotts 4-Step
        </button>
      </motion.div>
    </motion.div>
    </MotionConfig>
  )
}

// Per-category icon for at-a-glance cards. Maps to design icon names.
const HERO_PICK_ICONS: Record<string, LucideIcon> = {
  "Pre-emergent": SprayCan,
  Fertilizer: Leaf,
  "Post-emergent": SprayCan,
  Sprayer: SprayCan,
  "Soil test": FlaskConical,
  Mower: Scissors,
}

// At-a-glance hero tile selectors. Each ref points to one curated pick inside a
// SECTION so the hero name/price/image/Amazon URL stays in sync with the
// product card rendered in that section below. Lock state is derived from the
// section's `preview` flag plus the page-level `unlocked` state.
const HERO_PICK_REFS: {
  sectionId: string
  label: string
  pickIndex: number
  tone: "neutral" | "cool" | "warm" | "brand"
}[] = [
  { sectionId: "pre-emergent", label: "Pre-emergent", pickIndex: 0, tone: "brand" },
  { sectionId: "fertilizer", label: "Fertilizer", pickIndex: 0, tone: "brand" },
  { sectionId: "post-emergent", label: "Post-emergent", pickIndex: 0, tone: "cool" },
  { sectionId: "sprayer", label: "Sprayer", pickIndex: 1, tone: "neutral" },
  { sectionId: "soil-test", label: "Soil test", pickIndex: 0, tone: "warm" },
  { sectionId: "mower", label: "Mower", pickIndex: 0, tone: "neutral" },
]

// Per-section icon used in the TOC + at-a-glance tiles.
const SECTION_ICON_BY_ID: Record<string, LucideIcon> = {
  "pre-emergent": SprayCan,
  fertilizer: Leaf,
  "post-emergent": SprayCan,
  sprayer: SprayCan,
  "soil-test": FlaskConical,
  spreader: Leaf,
  seed: Sprout,
  iron: Droplets,
  mower: Scissors,
  aeration: Leaf,
  irrigation: Droplets,
  "wetting-agents": Droplets,
  avoid: XCircle,
  calendar: Clock,
}

// Tone used for the striped product-shot placeholder + product card image bg.
// Cool-season picks tint teal, warm-season tint amber, brand picks tint green.
const SECTION_TONE_BY_ID: Record<string, "neutral" | "cool" | "warm" | "brand"> = {
  "pre-emergent": "brand",
  fertilizer: "brand",
  "post-emergent": "cool",
  sprayer: "neutral",
  "soil-test": "neutral",
  spreader: "neutral",
  seed: "brand",
  iron: "brand",
  mower: "neutral",
  aeration: "neutral",
  irrigation: "neutral",
  "wetting-agents": "neutral",
  avoid: "neutral",
  calendar: "neutral",
}

// Friendly label used by the section-end InlineCapture ("N more X-style picks").
// Per-section override so the slug "post-emergent" doesn't render as the awkward
// "post emergent-style." Falls back to the section id with hyphens stripped.
const SECTION_CAPTURE_LABEL: Record<string, string> = {
  "post-emergent": "herbicide",
  "soil-test": "soil-test",
  "wetting-agents": "wetting-agent",
}

// Striped product-shot placeholder. Design rule: no fake illustrations.
function ProductPlaceholder({
  label = "product shot",
  tone = "neutral",
  className,
}: {
  label?: string
  tone?: "neutral" | "cool" | "warm" | "brand"
  className?: string
}) {
  const stripe: Record<string, string> = {
    neutral:
      "bg-[repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6_10px,#ffffff_10px,#ffffff_20px)] text-gray-500",
    cool: "bg-[repeating-linear-gradient(45deg,#ecfdf5,#ecfdf5_10px,#ffffff_10px,#ffffff_20px)] text-teal-700",
    warm: "bg-[repeating-linear-gradient(45deg,#fffbeb,#fffbeb_10px,#ffffff_10px,#ffffff_20px)] text-amber-700",
    brand:
      "bg-[repeating-linear-gradient(45deg,#f0fdf4,#f0fdf4_10px,#ffffff_10px,#ffffff_20px)] text-green-700",
  }
  return (
    <div
      className={cn(
        "flex items-center justify-center border border-gray-200 rounded-lg overflow-hidden",
        stripe[tone],
        className,
      )}
      aria-hidden="true"
    >
      <span className="font-mono text-[11px] tracking-wider bg-white/85 px-2 py-1 rounded">
        {label}
      </span>
    </div>
  )
}

function HeroPick({
  pick,
  label,
  sectionId,
  tone = "neutral",
  locked = false,
  onUnlock,
}: {
  pick: ProductPick
  label: string
  sectionId: string
  tone?: "neutral" | "cool" | "warm" | "brand"
  locked?: boolean
  onUnlock?: () => void
}) {
  const imageWell = (
    <div className="w-20 h-16 flex-shrink-0 flex items-center justify-center rounded-lg overflow-hidden border border-white/55 bg-white/70 relative">
      {pick.imageUrl ? (
        <Image
          src={pick.imageUrl}
          alt={locked ? `${label} pick` : pick.name}
          width={128}
          height={128}
          loading="lazy"
          className={cn(
            "max-h-full max-w-full object-contain p-1.5 transition-transform duration-200",
            locked && "blur-[6px] scale-110 opacity-80",
          )}
        />
      ) : (
        <ProductPlaceholder label="product shot" tone={tone} className="w-full h-full border-0" />
      )}
      {locked && pick.imageUrl && (
        <span aria-hidden="true" className="absolute inset-0 bg-white/30" />
      )}
    </div>
  )

  const body = (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-sm font-bold leading-tight tracking-tight line-clamp-2",
          locked ? "text-gray-400 blur-[4px] select-none" : "text-gray-900",
        )}
        aria-hidden={locked ? true : undefined}
      >
        {locked ? "████████████" : pick.name}
      </div>
      {pick.price && (
        <div className="mt-1 text-[12.5px] font-medium text-gray-600">{pick.price}</div>
      )}
    </div>
  )

  if (locked) {
    return (
      <HoverLift
        lift={0}
        pressScale={1}
        className="ds-glass relative h-full rounded-xl p-3 flex items-center gap-3"
      >
        {imageWell}
        {body}
        <button
          type="button"
          onClick={onUnlock}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-green-600 hover:bg-green-700 border border-green-700 rounded-full px-2 py-0.5 shadow-sm shadow-green-500/30 transition-colors"
          aria-label={`Unlock ${label} pick with email`}
        >
          <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
          Unlock
        </button>
      </HoverLift>
    )
  }

  return (
    <HoverLift
      lift={-2}
      pressScale={0.995}
      className="ds-glass h-full rounded-xl hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow"
    >
      <a
        href={`#${sectionId}`}
        className="h-full flex items-center gap-3 p-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
        aria-label={`Jump to ${label}: ${pick.name}`}
      >
        {imageWell}
        {body}
      </a>
    </HoverLift>
  )
}

// Four display tiers. Editor's Pick maps to Top Pick (green); other badges
// stay as a secondary chip alongside the tier pill.
type DisplayTier = "Top Pick" | "Best Value" | "Pro" | "Budget"
function displayTier(pick: ProductPick): DisplayTier {
  if (pick.badge === "Editor's Pick") return "Top Pick"
  return pick.tier as DisplayTier
}

function TierBadge({ tier }: { tier: DisplayTier }) {
  const styles: Record<DisplayTier, string> = {
    "Top Pick": "bg-green-600 text-white border-green-600",
    "Best Value": "bg-amber-400 text-gray-900 border-amber-400",
    Pro: "bg-gray-900 text-white border-gray-900",
    Budget: "bg-white text-gray-700 border-gray-300",
  }
  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-1 rounded-full border text-[10.5px] font-bold uppercase tracking-[0.08em] whitespace-nowrap",
        styles[tier],
      )}
    >
      {tier}
    </span>
  )
}

function EditorialChip({ badge }: { badge: Badge }) {
  if (!badge || badge === "Editor's Pick") return null
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/55 bg-white/55 backdrop-blur-md text-gray-700 text-[10.5px] font-semibold uppercase tracking-wider whitespace-nowrap">
      {badge}
    </span>
  )
}

function ProductCard({
  pick,
  onAffiliateClick,
  tone = "neutral",
}: {
  pick: ProductPick
  onAffiliateClick: (pick: ProductPick) => void
  tone?: "neutral" | "cool" | "warm" | "brand"
}) {
  const reduce = useReducedMotion()
  const tier = displayTier(pick)
  const isTopPick = tier === "Top Pick"
  // Denominator for the `inline` surface, which logged clicks with none. Same
  // surface key and product identifiers the click carries, so CTR joins.
  const impressionRef = useAffiliateImpression("inline", {
    article_slug: "lawn-products-guide-2026",
    product_name: pick.name,
    affiliate_link: pick.amazon,
  })
  return (
    <HoverLift
      lift={-2}
      pressScale={0.995}
      className={cn(
        "ds-glass h-full rounded-2xl transition-shadow",
        isTopPick
          ? "ring-1 ring-green-400/40 hover:shadow-[0_12px_28px_-6px_rgba(22,163,74,0.22)]"
          : "hover:shadow-md",
      )}
    >
    <a
      ref={impressionRef}
      href={pick.amazon}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      onClick={() => onAffiliateClick(pick)}
      className="group relative block rounded-2xl p-4 md:p-5 flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
    >
      {/* Tier pill — floats above the card edge */}
      <motion.div
        className="absolute -top-2.5 left-4"
        initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={FADE_TRANSITION_SOFT}
      >
        <TierBadge tier={tier} />
      </motion.div>

      {/* Image / placeholder */}
      <div className="mt-2 mb-3 h-32 md:h-36 flex items-center justify-center rounded-lg overflow-hidden border border-white/55 bg-white/70">
        {pick.imageUrl ? (
          <Image
            src={pick.imageUrl}
            alt={pick.name}
            width={176}
            height={176}
            loading="lazy"
            className="max-h-full max-w-full object-contain p-2 transition-transform duration-200 group-hover:scale-[1.04]"
          />
        ) : (
          <ProductPlaceholder label="product shot" tone={tone} className="w-full h-full border-0" />
        )}
      </div>

      {/* Editorial chip (Cult Favorite, Beginner Friendly, etc.) */}
      {pick.badge && pick.badge !== "Editor's Pick" && (
        <motion.div
          className="mb-2 flex flex-wrap gap-1.5"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ ...FADE_TRANSITION_SOFT, delay: 0.08 }}
        >
          <EditorialChip badge={pick.badge} />
        </motion.div>
      )}

      {/* Name + price baseline-aligned row */}
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] md:text-base font-bold text-gray-900 leading-snug tracking-tight group-hover:text-green-700 transition-colors">
          {pick.name}
        </h3>
        {pick.price && (
          <span className="text-[15px] md:text-base font-bold text-gray-900 tabular-nums whitespace-nowrap">
            {pick.price}
          </span>
        )}
      </div>

      {/* Why-it-wins blurb */}
      <p className="mt-2 text-[13px] md:text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
        {pick.blurb}
      </p>

      {/* Single CTA */}
      <div
        className={cn(
          "mt-4 inline-flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5 text-sm font-bold border transition-colors",
          isTopPick
            ? "bg-green-600 text-white border-green-600 group-hover:bg-green-700 group-hover:border-green-700 shadow-sm shadow-green-500/25"
            : "bg-white text-gray-900 border-gray-300 group-hover:border-green-400 group-hover:text-green-700",
        )}
      >
        {pick.isSearch ? "Find on Amazon" : "View on Amazon"}
        <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
      </div>
    </a>
    </HoverLift>
  )
}

// "Skip these" — red-tinted callout with × markers
function SkipList({ items }: { items: string[] }) {
  return (
    <Reveal y={8}>
      <div className="mt-8 rounded-xl border border-red-100 bg-red-50 p-5">
        <div className="flex items-center gap-2 mb-3">
          <XCircle className="w-[18px] h-[18px] text-red-600" strokeWidth={2.2} />
          <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-red-700">
            Skip these
          </h3>
        </div>
        <Stagger role="list" className="space-y-2" staggerChildren={0.04}>
          {items.map((item, i) => (
            <Stagger.Item key={i} role="listitem" className="flex gap-3 text-sm text-gray-800 leading-relaxed">
              <span className="flex-shrink-0 mt-0.5 w-[18px] h-[18px] rounded-full bg-white border border-red-200 flex items-center justify-center">
                <X className="w-2.5 h-2.5 text-red-600" strokeWidth={2.5} />
              </span>
              <span>{item}</span>
            </Stagger.Item>
          ))}
        </Stagger>
      </div>
    </Reveal>
  )
}

// "Pro tip" — quiet dark-bar quote-style block
function ProTip({ children }: { children: React.ReactNode }) {
  return (
    <Reveal y={6} delay={0.06}>
      <div className="ds-glass mt-5 rounded-lg border-l-4 border-l-gray-900 p-5">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-gray-900 mb-2">
          Pro tip
        </div>
        <div className="text-[14.5px] text-gray-700 leading-relaxed">{children}</div>
      </div>
    </Reveal>
  )
}

// Numbered section header — monospace rule + verdict callout
function SectionHeader({
  number,
  title,
  subtitle,
  verdict,
  total = 14,
}: {
  number: number
  title: string
  subtitle: string
  verdict?: string
  total?: number
}) {
  const n = String(number).padStart(2, "0")
  return (
    <Reveal y={10}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-[11.5px] font-bold tracking-[0.05em] text-gray-500">
            {n} <span className="text-gray-300">/</span> {String(total).padStart(2, "0")}
          </span>
          <span className="flex-1 h-px bg-gray-200" />
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-[36px] font-extrabold text-gray-900 leading-[1.1] tracking-tight">
          {title}
        </h2>
        <p className="mt-2 text-base sm:text-lg text-gray-500">{subtitle}</p>

        {verdict && (
          <Reveal y={6} delay={0.08}>
            <div className="mt-5 flex items-start gap-3 rounded-lg border border-green-100 border-l-4 border-l-green-600 bg-green-50 px-4 py-3.5">
              <span className="flex-shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full bg-green-600 text-white text-[10.5px] font-extrabold uppercase tracking-[0.06em] whitespace-nowrap">
                The verdict
              </span>
              <div className="text-[15px] font-medium text-gray-900 leading-relaxed">
                {verdict}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </Reveal>
  )
}

// Inline email capture at the end of every preview section
function InlineCapture({
  count,
  label,
  email,
  onChange,
  onSubmit,
  isSubmitting,
  suggestion,
  onApplySuggestion,
}: {
  count: number
  label: string
  email: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: (e?: React.FormEvent) => void
  isSubmitting: boolean
  suggestion: string | null
  onApplySuggestion: () => void
}) {
  return (
    <Reveal y={10} soft>
      <form
        onSubmit={onSubmit}
        className="mt-8 rounded-xl border border-green-100 bg-linear-to-b from-green-50 to-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5"
      >
        <div className="flex-1 min-w-0 sm:pt-1">
          <div className="text-[11.5px] font-bold uppercase tracking-[0.08em] text-green-700">
            Liked this section?
          </div>
          <div className="mt-1 text-[15px] sm:text-base font-bold text-gray-900 leading-snug tracking-tight">
            {count} more {label}-style picks in your inbox in 10 seconds.
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-shrink-0 sm:min-w-[360px]">
          <div className="flex gap-2">
            <Input
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              value={email}
              onChange={onChange}
              className="flex-1 h-11 px-3 text-base border border-gray-300 rounded-lg bg-white focus:border-green-500 focus:ring-0"
              style={{ boxShadow: "none" }}
              aria-label="Email address"
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              className="h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 font-bold whitespace-nowrap shadow-sm shadow-green-500/25"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send<ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </div>
          {suggestion && (
            <EmailSuggestionHint
              suggestion={suggestion}
              onApply={onApplySuggestion}
            />
          )}
        </div>
      </form>
    </Reveal>
  )
}

// Mailcheck-style "Did you mean ...?" hint. Shared between hero, gate,
// exit-intent, and inline captures so a `gmial.com` typo gets the same nudge
// regardless of which surface the user is on.
function EmailSuggestionHint({
  suggestion,
  onApply,
}: {
  suggestion: string
  onApply: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={FADE_TRANSITION}
      className="mt-2 text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-snug"
    >
      Did you mean{" "}
      <button
        type="button"
        onClick={onApply}
        className="font-semibold underline underline-offset-2 decoration-amber-400 hover:decoration-amber-700 hover:text-amber-950 transition-colors"
      >
        {suggestion}
      </button>
      ?
    </motion.div>
  )
}

function Section({
  section,
  onAffiliateClick,
  capture,
}: {
  section: GuideSection
  onAffiliateClick: (pick: ProductPick) => void
  capture?: {
    email: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onSubmit: (e?: React.FormEvent) => void
    isSubmitting: boolean
    suggestion: string | null
    onApplySuggestion: () => void
  }
}) {
  const tone = SECTION_TONE_BY_ID[section.id] ?? "neutral"
  const captureCount = section.picks ? Math.max(3, 9 - section.picks.length) : 9
  const captureLabel =
    SECTION_CAPTURE_LABEL[section.id] ?? section.id.replace(/-/g, " ")

  return (
    <section id={section.id} className="scroll-mt-24">
      <SectionHeader
        number={section.number}
        title={section.title}
        subtitle={section.subtitle}
        verdict={section.verdict}
      />

      <Reveal y={8} delay={0.04}>
        <p className="text-[16px] text-gray-700 leading-relaxed mb-6 max-w-3xl">
          {section.intro}
        </p>
      </Reveal>

      {section.cta && (() => {
        const cta = section.cta
        const isHd = cta.vendor === "homedepot"
        const href = isHd ? homeDepotUrl(cta.url) : cta.url
        const affiliateActive = isHd && HOMEDEPOT_AFFILIATE_ACTIVE
        return (
          <Reveal y={6} delay={0.06}>
            <a
              href={href}
              target="_blank"
              rel={affiliateActive ? "noopener noreferrer nofollow sponsored" : "noopener noreferrer"}
              onClick={() =>
                trackEvent("section_cta_clicked", {
                  section_id: section.id,
                  vendor: cta.vendor,
                  label: cta.label,
                  destination_url: cta.url,
                  affiliate_active: affiliateActive,
                })
              }
              className="group mb-6 inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 text-white px-5 py-3 text-sm font-bold shadow-sm shadow-green-500/25 transition-colors"
              data-vendor={cta.vendor}
              data-affiliate={affiliateActive ? "true" : undefined}
            >
              {cta.label}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
            </a>
          </Reveal>
        )
      })()}

      {section.picks && section.picks.length > 0 && (
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mt-3">
          {section.picks.map((pick) => (
            <Stagger.Item key={`${section.id}-${pick.name}`}>
              <ProductCard
                pick={pick}
                onAffiliateClick={onAffiliateClick}
                tone={tone}
              />
            </Stagger.Item>
          ))}
        </Stagger>
      )}

      {section.avoid && section.avoid.length > 0 && <SkipList items={section.avoid} />}

      {section.tip && <ProTip>{section.tip}</ProTip>}

      {capture && section.preview && (
        <InlineCapture
          count={captureCount}
          label={captureLabel}
          email={capture.email}
          onChange={capture.onChange}
          onSubmit={capture.onSubmit}
          isSubmitting={capture.isSubmitting}
          suggestion={capture.suggestion}
          onApplySuggestion={capture.onApplySuggestion}
        />
      )}
    </section>
  )
}

// Split each entry's "Phase (Months)" label into its month-band and phase parts.
// E.g. "Late winter / early spring (Feb – Mar)" → { months: "Feb – Mar", phase: "Late winter / early spring" }
function splitCalendarWindow(window: string): { months: string; phase: string } {
  const m = window.match(/^(.*?)\s*\((.*)\)\s*$/)
  if (!m) return { months: "", phase: window }
  return { phase: m[1].trim(), months: m[2].trim() }
}

function CalendarLane({
  season,
  title,
  subtitle,
  entries,
}: {
  season: "cool" | "warm"
  title: string
  subtitle: string
  entries: { window: string; items: string[] }[]
}) {
  const tone =
    season === "cool"
      ? { headerBg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-600" }
      : { headerBg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-600" }
  const SeasonIcon = season === "cool" ? Snowflake : Sun
  return (
    <div className="ds-glass rounded-xl overflow-hidden">
      <div
        className={cn(
          "flex items-center gap-3 px-5 py-4 border-b border-white/45",
          tone.headerBg,
        )}
      >
        <div className="w-9 h-9 rounded-lg bg-white/55 backdrop-blur-md border border-white/55 flex items-center justify-center">
          <SeasonIcon className={cn("w-[18px] h-[18px]", tone.text)} strokeWidth={2} />
        </div>
        <div>
          <div className={cn("text-[11.5px] font-bold uppercase tracking-[0.08em]", tone.text)}>
            {title}
          </div>
          <div className="text-base font-bold text-gray-900 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <Stagger staggerChildren={0.04}>
        {entries.map((entry, i) => {
          const { months, phase } = splitCalendarWindow(entry.window)
          return (
            <Stagger.Item
              key={entry.window}
              className={cn(
                "grid grid-cols-[minmax(0,100px)_1fr] gap-4 px-5 py-4",
                i !== 0 && "border-t border-dashed border-white/45",
              )}
            >
              <div>
                <div className={cn("font-mono text-[10.5px] font-bold tracking-[0.06em] uppercase", tone.text)}>
                  {months || entry.window}
                </div>
                <div className="text-[13px] font-semibold text-gray-900 mt-1 tracking-tight">
                  {phase}
                </div>
              </div>
              <ul className="space-y-1.5">
                {entry.items.map((item, j) => (
                  <li key={j} className="flex gap-2 text-[13px] text-gray-700 leading-relaxed">
                    <span
                      className={cn(
                        "flex-shrink-0 mt-[7px] w-1.5 h-1.5 rounded-full",
                        tone.dot,
                      )}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Stagger.Item>
          )
        })}
      </Stagger>
    </div>
  )
}

function TrustedResources() {
  return (
    <section id="trusted-resources" className="mt-16 scroll-mt-24">
      <Reveal y={10}>
        <div className="flex items-start gap-3 md:gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="relative w-11 h-11 md:w-12 md:h-12 rounded-xl bg-linear-to-br from-green-600 to-emerald-600 text-white flex items-center justify-center shadow-md shadow-green-200/70">
              <BookOpen className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.2} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-[1.1] tracking-tight">
              Trusted resources
            </h2>
            <p className="text-sm sm:text-base text-green-700 font-semibold mt-1.5">
              The university research we cross-check these picks against
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal y={8} delay={0.06}>
        <p className="text-gray-700 leading-relaxed mb-6">
          Every link below points to a university cooperative extension program. These are the same materials professional turfgrass programs train from, written by the people who run the regional cultivar trials and run the diagnostic labs your state's pros submit samples to. If a pick on this page disagrees with one of these sources, the source wins.
        </p>
      </Reveal>

      <Stagger className="space-y-5" staggerChildren={0.06}>
        {TRUSTED_RESOURCES.map((group) => (
          <Stagger.Item key={group.category}>
            <HoverLift lift={-2} pressScale={1} className="rounded-xl">
              <div className="ds-glass rounded-xl transition-shadow hover:shadow-md">
                <div className="p-5 md:p-6">
                  <div className="mb-4">
                    <h3 className="text-base md:text-lg font-extrabold text-gray-900 leading-snug mb-1.5">
                      {group.category}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{group.description}</p>
                  </div>
                  <ul className="space-y-3">
                    {group.links.map((link) => (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-start gap-2.5 rounded-lg -mx-2 px-2 py-1.5 hover:bg-green-50 transition-colors"
                        >
                          <ExternalLink
                            className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-1"
                            strokeWidth={2.4}
                            aria-hidden="true"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-900 group-hover:text-green-700 leading-snug transition-colors">
                              {link.name}
                            </div>
                            <p className="text-[13px] text-gray-600 leading-snug mt-0.5">
                              {link.blurb}
                            </p>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </HoverLift>
          </Stagger.Item>
        ))}
      </Stagger>

      <Reveal y={6} delay={0.12}>
        <p className="mt-5 text-xs text-gray-500 italic leading-relaxed">
          Links go to .edu and university extension sources. We get no compensation for sending you there — they're listed because they're the best free turf education on the internet.
        </p>
      </Reveal>
    </section>
  )
}
